import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { heartGet, heartSetOn, heartSubscribe, heartNative } from './heart';   // [heart]
import { useMusicPlayer } from './MusicPlayer';
import EditorPanel from './EditorPanel';
import CalendarView from './CalendarView';

// ========== 语音条（模块级组件：身份稳定，Chat重渲染不会重挂载丢状态） ==========
  var fmtVoiceDuration = function(sec) {
    var m = Math.floor(sec / 60);
    var s = Math.floor(sec % 60);
    return m + ':' + s.toString().padStart(2, '0');
  };

  // ========== 语音条渲染 ==========
  var VoiceBar = function(props) {
    var msg = props.msg;
    var audioSrc = msg._voice_blob_url || msg.audio_url;
    var duration = msg._voice_duration || 0;
    var transcript = msg._voice_transcript || '';
    var emotion = msg._voice_emotion || msg.voice_emotion || 'neutral';
    var isUser = msg.sender === 'zhizhi';
    // 从text里提取duration和transcript（历史消息）
    if (!duration && msg.text) {
      var durMatch = msg.text.match(/\uD83C\uDFA4 (\d+)s/);
      if (durMatch) duration = parseInt(durMatch[1]);
      var durMatch2 = msg.text.match(/\uD83C\uDFA4 (\d+:\d+)/);
      if (durMatch2) { var pts = durMatch2[1].split(':'); duration = parseInt(pts[0])*60 + parseInt(pts[1]); }
      if (!transcript) {
        var tMatch = msg.text.match(/\| ([\s\S]+)$/);   // [send-voice] 他的语音字幕是两行（英文\n中文）
        if (tMatch) transcript = tMatch[1].trim();
      }
    }
    var [playing, setPlaying] = React.useState(false);
    var [progress, setProgress] = React.useState(0);
    var [showTranscript, setShowTranscript] = React.useState(!isUser);   // [send-voice] 他发的语音字幕默认打开
    var audioRef = React.useRef(null);
    var animRef = React.useRef(null);
    // 卸载时停掉播放和动画（切窗口/切视图时语音不再幽灵续播）
    React.useEffect(function() {
      return function() {
        if (animRef.current) cancelAnimationFrame(animRef.current);
        if (audioRef.current) { try { audioRef.current.pause(); } catch(e) {} audioRef.current = null; }
      };
    }, []);
    var BARS = 28;
    var waveVals = React.useMemo(function() {
      var vals = [];
      for (var i = 0; i < BARS; i++) {
        var pos = i / BARS;
        var envelope = Math.sin(pos * Math.PI) * 0.6 + 0.15;
        var noise = (Math.random() * 0.5 + 0.5);
        vals.push(Math.max(0.08, Math.min(1, envelope * noise)));
      }
      return vals;
    }, [msg.id]);

    var togglePlay = function() {
      if (!audioRef.current) {
        audioRef.current = new Audio(audioSrc);
        audioRef.current.addEventListener('ended', function() {
          setPlaying(false); setProgress(0);
          if (animRef.current) cancelAnimationFrame(animRef.current);
        });
      }
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
        if (animRef.current) cancelAnimationFrame(animRef.current);
      } else {
        audioRef.current.play();
        setPlaying(true);
        var tick = function() {
          if (audioRef.current && audioRef.current.duration) {
            setProgress(audioRef.current.currentTime / audioRef.current.duration);
          }
          animRef.current = requestAnimationFrame(tick);
        };
        tick();
      }
    };

    var seekTo = function(e) {
      if (!audioRef.current || !audioRef.current.duration) return;
      var rect = e.currentTarget.getBoundingClientRect();
      var pct = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = pct * audioRef.current.duration;
      setProgress(pct);
    };

    var emoEmoji = { happy: '\uD83D\uDE0A', sad: '\uD83D\uDE22', angry: '\uD83D\uDE20', neutral: '', surprised: '\uD83D\uDE32', fearful: '\uD83D\uDE28', disgusted: '\uD83E\uDD22' };
    var playedIdx = Math.floor(progress * BARS);
    var currentTime = audioRef.current && playing ? Math.floor(audioRef.current.currentTime) : 0;
    var displayTime = playing ? fmtVoiceDuration(currentTime) : fmtVoiceDuration(duration || 0);

    return React.createElement('div', { className: 'voce-wrap' },
      React.createElement('div', { className: 'voce-bar' + (isUser ? ' voce-user' : ' voce-aiden') },
        React.createElement('button', { className: 'voce-play', onClick: togglePlay },
          playing
            ? React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 16 16', fill: '#fff' },
                React.createElement('rect', { x: 3, y: 2, width: 4, height: 12 }),
                React.createElement('rect', { x: 9, y: 2, width: 4, height: 12 })
              )
            : React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 16 16', fill: '#fff' },
                React.createElement('polygon', { points: '4,2 14,8 4,14' })
              )
        ),
        React.createElement('div', { className: 'voce-waveform', onClick: seekTo },
          waveVals.map(function(v, i) {
            return React.createElement('div', {
              key: i,
              className: 'voce-wbar' + (i < playedIdx ? ' played' : ''),
              style: { height: Math.max(4, Math.round(v * 22)) + 'px' }
            });
          })
        ),
        React.createElement('div', { className: 'voce-meta' },
          React.createElement('span', { className: 'voce-dur' }, displayTime),
          emotion && emotion !== 'neutral' && emoEmoji[emotion] ? React.createElement('span', { className: 'voce-emo' }, emoEmoji[emotion] + ' ' + emotion) : null,
          transcript ? React.createElement('button', { className: 'voce-txt-btn', onClick: function() { setShowTranscript(!showTranscript); } }, '\u6587') : null
        )
      ),
      showTranscript && transcript ? React.createElement('div', { className: 'voce-transcript' },
        transcript.split('\n').map(function(line, i) {   // [send-voice] 第一行英文、第二行中文；中文淡一点
          return React.createElement('div', { key: i, style: i > 0 ? { opacity: 0.62, fontSize: '0.92em', marginTop: '3px' } : null }, line);
        })
      ) : null
    );
  };


// [chat-cache] 对话本地缓存：IndexedDB 一个库一张表，按 conversation_id 存整本；没有 IndexedDB 就当没缓存
var __cc = {
  db: null,
  open: function() {
    if (this.db) return Promise.resolve(this.db);
    var self = this;
    return new Promise(function(resolve) {
      try {
        if (!window.indexedDB) return resolve(null);
        var req = window.indexedDB.open('aizhi-chat-cache', 1);
        req.onupgradeneeded = function() { try { req.result.createObjectStore('convos'); } catch (e) {} };
        req.onsuccess = function() { self.db = req.result; resolve(self.db); };
        req.onerror = function() { resolve(null); };
      } catch (e) { resolve(null); }
    });
  },
  get: function(convoId) {
    return this.open().then(function(db) {
      if (!db) return null;
      return new Promise(function(resolve) {
        try {
          var tx = db.transaction('convos', 'readonly'); var rq = tx.objectStore('convos').get(String(convoId));
          rq.onsuccess = function() { resolve(rq.result || null); }; rq.onerror = function() { resolve(null); };
        } catch (e) { resolve(null); }
      });
    });
  },
  put: function(convoId, msgs) {
    var clean = (msgs || []).filter(function(m) {
      var id = m && m.id; return !(typeof id === 'string' && (id.indexOf('tmp-') === 0 || id.indexOf('err-') === 0 || id.indexOf('pending-') === 0));
    }).map(function(m) { var o = {}; Object.keys(m).forEach(function(k) { if (k[0] !== '_') o[k] = m[k]; }); return o; });
    return this.open().then(function(db) {
      if (!db) return;
      try { var tx = db.transaction('convos', 'readwrite'); tx.objectStore('convos').put({ ts: Date.now(), msgs: clean }, String(convoId)); } catch (e) {}
    });
  }
};

function Chat({ showAvatar }) {
  var navigate = useNavigate();
  var musicPlayer = useMusicPlayer();
  var [pendingMusic, setPendingMusic] = useState(null);
  var [convos, setConvos] = useState([]);
  var [currentConvo, setCurrentConvo] = useState(null);
  var [messages, setMessages] = useState([]);
  var [input, setInput] = useState('');
  var [loading, setLoading] = useState(true);
  var [viewMode, setViewMode] = useState('chat');
  var [headerExpanded, setHeaderExpanded] = useState(false);
  var [toolsOpen, setToolsOpen] = useState(false);
  var [chatFont, setChatFont] = useState(function() {
    return localStorage.getItem('chat_font') || 'serif';
  });
  var [starringId, setStarringId] = useState(null);
  var [starLabel, setStarLabel] = useState('');
  var [starredMsgs, setStarredMsgs] = useState([]);
  var [starredTab, setStarredTab] = useState('text');
  var [voiceCollection, setVoiceCollection] = useState([]);
  var [chatSearchQuery, setChatSearchQuery] = useState('');
  var [chatSearchResults, setChatSearchResults] = useState([]);
  var [chatSearching, setChatSearching] = useState(false);
  var [isExtended, setIsExtended] = useState(false);
  var [searchDateFrom, setSearchDateFrom] = useState('');
  var [searchDateTo, setSearchDateTo] = useState('');
  var [showSidebar, setShowSidebar] = useState(false);
  var [showMenu, setShowMenu] = useState(false);
  var [heartUI, setHeartUI] = useState(function() { var h = heartGet(); return { on: h.on, state: h.state, bpm: h.bpm }; });   // [heart]
  useEffect(function() { return heartSubscribe(function(ev) { setHeartUI(function(u) { if (ev.type === 'state') return { on: ev.state !== 'off', state: ev.state, bpm: ev.state === 'off' ? 0 : u.bpm }; if (ev.type === 'bpm') return { on: true, state: 'on', bpm: ev.bpm }; return u; }); }); }, []);   // [heart]
  var [renaming, setRenaming] = useState(null);
  var [renameText, setRenameText] = useState('');
  var [deleting, setDeleting] = useState(null);
  var [deleteStep, setDeleteStep] = useState(0);
  var [sending, setSending] = useState(false);
  var sendingRef = useRef(false);
  var messagesEndRef = useRef(null);
  var pollTimerRef = useRef(null);
  var chatBodyRef = useRef(null);
  // 搜索跳转：定位到本窗口内某条消息并高亮
  var pendingJumpRef = useRef(null);
  var jumpTries = useRef(0);
  var doJump = function() {
    var id = pendingJumpRef.current;
    if (!id) return;
    var el = chatBodyRef.current && chatBodyRef.current.querySelector('[data-mid="' + id + '"]');
    if (el) {
      el.scrollIntoView({ block: 'center' });
      el.classList.add('jump-flash');
      setTimeout(function() { el.classList.remove('jump-flash'); }, 1800);
      pendingJumpRef.current = null;
      jumpTries.current = 0;
      setTimeout(function() {  // 图片加载会顶位置，600ms后再校正一次
        var e2 = chatBodyRef.current && chatBodyRef.current.querySelector('[data-mid="' + id + '"]');
        if (e2) e2.scrollIntoView({ block: 'center' });
      }, 600);
    } else if (jumpTries.current < 12) {
      jumpTries.current += 1;
      setTimeout(doJump, 300);
    } else {
      pendingJumpRef.current = null;
      jumpTries.current = 0;
    }
  };
  var [showScrollBtn, setShowScrollBtn] = useState(false);
  var [modelName, setModelName] = useState('');
  var [imageFiles, setImageFiles] = useState([]);
  var [imagePreviews, setImagePreviews] = useState([]);
  var fileInputRef = useRef(null);
  var [docFiles, setDocFiles] = useState([]);
  var [docNames, setDocNames] = useState([]);
  var fileDocInputRef = useRef(null);
  var [videoFile, setVideoFile] = useState(null);
  var [videoPreviewName, setVideoPreviewName] = useState('');
  var [videoStatus, setVideoStatus] = useState('');
  var [expandedThinking, setExpandedThinking] = useState({});
  var [bubbleMode, setBubbleMode] = useState(true);
  // 新增 state
  var [copiedId, setCopiedId] = useState(null);
  var [editingId, setEditingId] = useState(null);
  var [editText, setEditText] = useState('');
  var [genSelection, setGenSelection] = useState({});
  var [editBranches, setEditBranches] = useState({});
  var [htmlPopupUrl, setHtmlPopupUrl] = useState(null);
  var [showNightPassForm, setShowNightPassForm] = useState(false);
  var [nightPassApproved, setNightPassApproved] = useState(false);
  var [nightPassRejected, setNightPassRejected] = useState(false);
  var [fullscreenImg, setFullscreenImg] = useState(null);
  var [showEditor, setShowEditor] = useState(false);
  var [streamEnabled, setStreamEnabled] = useState(false);
  var streamTimerRef = useRef(null);
  var [showSearch, setShowSearch] = useState(false);
  var [searchQuery, setSearchQuery] = useState('');
  var [searchResults, setSearchResults] = useState([]);
  var [searchLoading, setSearchLoading] = useState(false);
  var [browserUrl, setBrowserUrl] = useState(null);
  var [avatarMe, setAvatarMe] = useState(null);
  var [avatarHim, setAvatarHim] = useState(null);
  var [chatBgUrl, setChatBgUrl] = useState(function() { return localStorage.getItem('chat_bg_url') || ''; });
  var [glassBlur, setGlassBlur] = useState(function() { return parseInt(localStorage.getItem('glass_blur') || '0'); });

  // 壁纸挂在body class上，App.css据此给.app设背景；模糊已在保存时烘焙进图片
  useEffect(function() {
    if (chatBgUrl) {
      document.body.classList.add('glass-bg-active');
      document.body.style.setProperty('--chat-bg-img', 'url(' + chatBgUrl + ')');
    } else {
      document.body.classList.remove('glass-bg-active');
      document.body.style.removeProperty('--chat-bg-img');
    }
    return function() {
      document.body.classList.remove('glass-bg-active');
      document.body.style.removeProperty('--chat-bg-img');
    };
  }, [chatBgUrl]);

  // Settings改了壁纸后即时同步（不用等重新进聊天页）
  useEffect(function() {
    var onBgChange = function() { setChatBgUrl(localStorage.getItem('chat_bg_url') || ''); };
    window.addEventListener('aizhi-bg-change', onBgChange);
    return function() { window.removeEventListener('aizhi-bg-change', onBgChange); };
  }, []);
  var [nicknameMe, setNicknameMe] = useState('\u679D');
  var [nicknameHim, setNicknameHim] = useState('\u664F');
  var [enterToSend, setEnterToSend] = useState(function() {
    return localStorage.getItem('split_send') === 'true';
  });
  var [pendingMsgs, setPendingMsgs] = useState(function() {
    try { var s = localStorage.getItem('aizhi_pending_msgs'); return s ? JSON.parse(s) : []; } catch(e) { return []; }
  });
  // 分条消息持久化：页面reload不丢（图片/文件对象无法持久化，只存文字和表情包）
  useEffect(function() {
    var saveable = pendingMsgs.filter(function(m) { return !m.image_file && !m.doc_file; });
    if (saveable.length > 0) localStorage.setItem('aizhi_pending_msgs', JSON.stringify(saveable));
    else localStorage.removeItem('aizhi_pending_msgs');
  }, [pendingMsgs]);

  // ========== 语音消息 voce ==========
  var [isRecording, setIsRecording] = useState(false);
  var [recSeconds, setRecSeconds] = useState(0);
  var [voiceUploading, setVoiceUploading] = useState(false);
  var mediaRecorderRef = useRef(null);
  var audioChunksRef = useRef([]);
  var recTimerRef2 = useRef(null);
  var analyserRef = useRef(null);
  var animFrameRef = useRef(null);
  var audioCtxRef = useRef(null);
  var [recWaveBars, setRecWaveBars] = useState(new Array(32).fill(4));
  var recSecondsRef = useRef(0);

  // ========== TTS 语音播报 ==========
  var [ttsEnabled, setTtsEnabled] = useState(false);
  var [ttsLoadingId, setTtsLoadingId] = useState(null);
  var [ttsPlayingId, setTtsPlayingId] = useState(null);
  var ttsAudioRef = useRef(null);
  var ttsCacheRef = useRef({});

  useEffect(function() {
    fetch((process.env.REACT_APP_API_URL || '') + '/api/profile-settings')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var g = function(k) { var i = data.find(function(d) { return d.key === k; }); return i ? i.value : null; };
        if (g('avatar_me')) setAvatarMe(g('avatar_me'));
        if (g('avatar_him')) setAvatarHim(g('avatar_him'));
        if (g('nickname_me')) setNicknameMe(g('nickname_me'));
        if (g('nickname_him')) setNicknameHim(g('nickname_him'));
        if (g('tts_enabled')) setTtsEnabled(g('tts_enabled') === 'true');
        if (g('chat_bg_url')) { setChatBgUrl(g('chat_bg_url')); localStorage.setItem('chat_bg_url', g('chat_bg_url')); }
        if (g('glass_blur') !== null) { var v = parseInt(g('glass_blur') || '0'); setGlassBlur(v); localStorage.setItem('glass_blur', String(v)); }
      })
      .catch(function(e) { console.log('[profile-settings] 头像/昵称加载失败:', e); });
  }, []);

  // ========== 搜索和内置浏览器 ==========
  var doSearch = async function() {
    if (!searchQuery.trim() || searchLoading) return;
    setSearchLoading(true);
    setSearchResults([]);
    try {
      var resp = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery.trim() })
      });
      var data = await resp.json();
      setSearchResults(data.results || []);
    } catch(e) {
      console.error('Search failed:', e);
    }
    setSearchLoading(false);
  };

  var openBrowser = function(url) {
    setBrowserUrl(url);
    setShowSearch(false);
    setShowMenu(false);
  };

  var closeBrowser = function() {
    setBrowserUrl(null);
  };

  var openSearchPanel = function() {
    // iOS native环境：直接打开内置浏览器
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeAction) {
      window.webkit.messageHandlers.nativeAction.postMessage('openBrowser');
      setShowMenu(false);
      return;
    }
    // Mac浏览器fallback：用搜索面板
    setShowSearch(true);
    setShowMenu(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  var openHtmlPopup = async function(url) {
    try {
      var resp = await fetch(url);
      var buffer = await resp.arrayBuffer();
      var blob = new Blob([buffer], { type: 'text/html; charset=utf-8' });
      var blobUrl = URL.createObjectURL(blob);
      setHtmlPopupUrl(blobUrl);
    } catch (e) {
      console.error('Failed to load HTML:', e);
      window.open(url, '_blank');
    }
  };

  var downloadAttachment = async function(e, url, filename) {
    e.stopPropagation();
    try {
      var resp = await fetch(url);
      var blob = await resp.blob();
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) { window.open(url, '_blank'); }
  };

  var shareAttachment = async function(e, url, filename) {
    e.stopPropagation();
    if (navigator.share) {
      try {
        var resp = await fetch(url);
        var blob = await resp.blob();
        var file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
        await navigator.share({ files: [file], title: filename });
      } catch (err) {
        try { await navigator.share({ title: filename, url: url }); } catch (e2) {}
      }
    } else {
      window.open(url, '_blank');
    }
  };

  var closeHtmlPopup = function() {
    if (htmlPopupUrl) URL.revokeObjectURL(htmlPopupUrl);
    setHtmlPopupUrl(null);
  };
  var [showStickerPanel, setShowStickerPanel] = useState(false);
  var [editingSticker, setEditingSticker] = useState(null);
  var [editStickerName, setEditStickerName] = useState('');
  var [editStickerDesc, setEditStickerDesc] = useState('');
  var [stickerList, setStickerList] = useState([]);
  var stickerFileRef = useRef(null);
  var [deleteConfirmSticker, setDeleteConfirmSticker] = useState(null);

  useEffect(function() {
    if (!loading && messages.length > 0) {
      if (pendingJumpRef.current) { setTimeout(doJump, 150); return; }
      setTimeout(function() {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, loading]);

  useEffect(function() {
    var el = chatBodyRef.current;
    if (!el) return;
    var handleScroll = function() {
      var distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(distFromBottom > 300);
    };
    el.addEventListener('scroll', handleScroll);
    return function() { el.removeEventListener('scroll', handleScroll); };
  }, [loading]);

  var scrollToBottom = function() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(function() { loadConvos(); loadBubbleMode(); }, []);
  useEffect(function() {
    fetch('/api/profile-settings').then(function(r) { return r.json(); }).then(function(data) {
      var se = data.find(function(d) { return d.key === 'stream_enabled'; });
      if (se) setStreamEnabled(se.value === 'true');
    }).catch(function(e) { console.log('[profile-settings] stream_enabled加载失败:', e); });
  }, []);
  useEffect(function() { return function() { if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; } }; }, []);

  // 键盘适配（iOS WKWebView）
  useEffect(function() {
    var root = document.documentElement;
    var cachedH = parseFloat(localStorage.getItem('kb_h') || '0');

    // 方案1：Swift键盘预告（最快）
    var onKb = function(e) {
      var d = e.detail;
      var h = d.h || 0;
      if (d.show && h > 0) {
        localStorage.setItem('kb_h', String(h));
        root.style.setProperty('--kb-h', h + 'px');
        root.style.setProperty('--kb-dur', (d.dur || 0.25) + 's');
        document.body.classList.add('kb-open');
      } else {
        root.style.setProperty('--kb-h', '0px');
        document.body.classList.remove('kb-open');
      }
    };
    window.addEventListener('kb', onKb);

    // 方案2：visualViewport后备（非native环境）
    var vv = window.visualViewport;
    var fullH = window.innerHeight;
    var onResize = function() {
      if (!vv) return;
      var kbH = fullH - vv.height;
      if (kbH > 50) {
        root.style.setProperty('--kb-h', kbH + 'px');
        localStorage.setItem('kb_h', String(kbH));
        document.body.classList.add('kb-open');
      } else {
        root.style.setProperty('--kb-h', '0px');
        document.body.classList.remove('kb-open');
      }
    };
    if (vv) vv.addEventListener('resize', onResize);

    return function() {
      window.removeEventListener('kb', onKb);
      if (vv) vv.removeEventListener('resize', onResize);
      root.style.setProperty('--kb-h', '0px');
      document.body.classList.remove('kb-open');
    };
  }, []);

  // [corvin-seg] 等回复的时候每 2.5 秒看一眼：哥哥在工具调用之间说的话，先上屏，不用等他全部做完
  useEffect(function() {
    if (!sending || !currentConvo) return;
    var segConvoId = currentConvo.id;
    var segSince = new Date(Date.now() - 120000).toISOString();   // 放宽两分钟，容忍手机和服务器的钟差；已经在屏上的按 id 去重
    var segTimer = setInterval(async function() {
      try {
        var r = await supabase.from('messages').select('*')
          .eq('conversation_id', segConvoId).eq('sender', 'aiden')
          .is('edit_branch_id', null).neq('is_active', false)
          .gt('created_at', segSince)
          .order('created_at', { ascending: true }).limit(20);
        var rows = r.data || [];
        if (!rows.length) return;
        setMessages(function(p) {
          var have = {};
          p.forEach(function(m) { have[m.id] = true; });
          var add = rows.filter(function(m) { return !have[m.id]; });
          return add.length ? p.concat(add) : p;
        });
      } catch (e) {}
    }, 2500);
    return function() { clearInterval(segTimer); };
  }, [sending, currentConvo]);

  // 后台轮询：每30秒检查新消息（主动发消息、peek评论等）
  // [bg-poll-delta] 只问"比我手上最新那条更新的"，没有就空手回；不再每30秒把整本5000条搬回来（Supabase egress 大头）
  var bgPollRef = useRef(null);
  var messagesRef = useRef(messages);
  useEffect(function() { messagesRef.current = messages; }, [messages]);
  // [chat-cache] 屏上的列表变了就落到本地（2 秒防抖）
  var ccTimerRef = useRef(null);
  useEffect(function() {
    if (!currentConvo || !messages || !messages.length) return;
    if (ccTimerRef.current) clearTimeout(ccTimerRef.current);
    var cid = currentConvo.id, snap = messages;
    ccTimerRef.current = setTimeout(function() { __cc.put(cid, snap); }, 2000);
    return function() { if (ccTimerRef.current) clearTimeout(ccTimerRef.current); };
  }, [messages, currentConvo]);
  useEffect(function() {
    if (bgPollRef.current) clearInterval(bgPollRef.current);
    if (!currentConvo) return;
    var convoId = currentConvo.id;
    bgPollRef.current = setInterval(async function() {
      if (sendingRef.current) return;
      try {
        var cur = messagesRef.current || [];
        var since = null;
        for (var i = cur.length - 1; i >= 0; i--) {
          var m = cur[i];
          var id = m && m.id;
          var isLocal = typeof id === 'string' && (id.indexOf('tmp-') === 0 || id.indexOf('err-') === 0 || id.indexOf('pending-') === 0);
          if (!isLocal && m.created_at && (!since || m.created_at > since)) since = m.created_at;
        }
        var q = supabase.from('messages').select('*')
          .eq('conversation_id', convoId)
          .is('edit_branch_id', null)
          .order('created_at', { ascending: true }).limit(500);
        if (since) q = q.gt('created_at', since);
        var r = await q;
        var rows = r.data || [];
        if (!rows.length) return;
        setMessages(function(p) {
          var have = {};
          p.forEach(function(x) { have[x.id] = true; });
          var add = rows.filter(function(x) { return !have[x.id]; });
          return add.length ? dedupeMessages(p.concat(add)) : p;
        });
      } catch (e) { console.log('[bgPoll] 后台轮询失败:', e); }
    }, 30000);
    return function() { if (bgPollRef.current) clearInterval(bgPollRef.current); };
  }, [currentConvo]);

  // [bg-poll-count] 每5分钟只问一句"一共几条"（只回数字不回内容）；对不上（后台删过）就整本重读一次
  var bgCountRef = useRef(null);
  useEffect(function() {
    if (bgCountRef.current) clearInterval(bgCountRef.current);
    if (!currentConvo) return;
    var convoId = currentConvo.id;
    bgCountRef.current = setInterval(async function() {
      if (sendingRef.current) return;
      try {
        var r = await supabase.from('messages').select('id', { count: 'exact', head: true })
          .eq('conversation_id', convoId)
          .is('edit_branch_id', null);
        if (r.error || typeof r.count !== 'number') return;
        var cur = messagesRef.current || [];
        var local = 0;
        cur.forEach(function(m) {
          var id = m && m.id;
          if (typeof id === 'string' && (id.indexOf('tmp-') === 0 || id.indexOf('err-') === 0 || id.indexOf('pending-') === 0)) local++;
        });
        if (r.count === cur.length - local) return;
        if (sendingRef.current) return;
        var full = await supabase.from('messages').select('*')
          .eq('conversation_id', convoId)
          .is('edit_branch_id', null)
          .order('created_at', { ascending: true }).limit(5000);
        if (full.data && !sendingRef.current) setMessages(dedupeMessages(full.data));
      } catch (e) { console.log('[bgPoll] 计数校对失败:', e); }
    }, 300000);
    return function() { if (bgCountRef.current) clearInterval(bgCountRef.current); };
  }, [currentConvo]);
  useEffect(function() {
    var reportError = function(error, stack) {
      try { fetch('/api/dev/error-report', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ error: String(error), stack: stack || '', url: window.location.href, timestamp: new Date().toISOString() }) }); } catch(e) {}
    };
    var onError = function(e) { reportError(e.message, e.error && e.error.stack); };
    var onReject = function(e) { reportError('Unhandled rejection: ' + (e.reason && e.reason.message || e.reason), e.reason && e.reason.stack); };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onReject);
    return function() { window.removeEventListener('error', onError); window.removeEventListener('unhandledrejection', onReject); };
  }, []);
  useEffect(function() {
    // 图库「带去聊天」：把那张图放进待发区
    try {
      var raw = sessionStorage.getItem('gallery_bring');
      if (raw) {
        sessionStorage.removeItem('gallery_bring');
        var g = JSON.parse(raw);
        if (g && g.url) { setImageFiles(function(prev) { return prev.concat([g.url]); }); setImagePreviews(function(prev) { return prev.concat([g.url]); }); }
      }
    } catch (e) {}
  }, []);

  useEffect(function() {
    // 头部状态：订阅线显示 Claude Code，API 线显示中转站模型名；回到页面时重读，切线后不用刷新
    var loadModelLabel = function() {
      fetch('/api/profile-settings').then(function(r) { return r.json(); }).then(function(data) {
        var cp = Array.isArray(data) ? data.find(function(d) { return d.key === 'chat_provider'; }) : null;
        if (cp && cp.value === 'claude_code') { setModelName('\u8BA2\u9605 \u00B7 Claude Code \u00B7 Opus 4.6'); return; }
        supabase.from('settings').select('value').eq('key', 'model').single().then(function(r) {
          if (r && r.data && r.data.value) setModelName(r.data.value);
        });
      }).catch(function(e) {
        console.log('[header] chat_provider 读取失败，退回模型名:', e);
        supabase.from('settings').select('value').eq('key', 'model').single().then(function(r) {
          if (r && r.data && r.data.value) setModelName(r.data.value);
        });
      });
    };
    loadModelLabel();
    var onVis = function() { if (document.visibilityState === 'visible') loadModelLabel(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', loadModelLabel);
    return function() { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', loadModelLabel); };
  }, []);

  var loadBubbleMode = async function() {
    var r = await supabase.from('settings').select('value').eq('key', 'bubble_mode').single();
    if (r.data) setBubbleMode(r.data.value === 'true');
  };

  var toggleBubbleMode = async function() {
    var newMode = !bubbleMode;
    setBubbleMode(newMode);
    var { data: existing } = await supabase.from('settings').select('id').eq('key', 'bubble_mode').single();
    if (existing) {
      await supabase.from('settings').update({ value: String(newMode) }).eq('key', 'bubble_mode');
    } else {
      await supabase.from('settings').insert([{ key: 'bubble_mode', value: String(newMode) }]);
    }
  };

  var loadConvos = async function() {
    var r = await supabase.from('conversations').select('*').order('updated_at', { ascending: false });
    var list = r.data || [];
    setConvos(list);
    if (list.length > 0 && !currentConvo) {
      var firstDaily = null;
      for (var fi = 0; fi < list.length; fi++) { if (list[fi].room !== 'silas' && list[fi].room !== 'lounge') { firstDaily = list[fi]; break; } }
      if (firstDaily) {
        setCurrentConvo(firstDaily);
        loadMessages(firstDaily.id);
        setIsExtended(firstDaily.extended || false);
      }
    } else if (list.length === 0) {
      var n = await supabase.from('conversations').insert([{ title: '\u65B0\u7A97\u53E3' }]).select();
      if (n.data) { setConvos(n.data); setCurrentConvo(n.data[0]); }
      setLoading(false);
    } else { setLoading(false); }
  };

  // ── 房间二：Silas（血统制：认room字段，不再认单一id） ──
  var enterSilas = async function() {
    try {
      var resp = await fetch('/api/silas/ensure', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      var d = await resp.json();
      if (!d || !d.conversation_id) return;
      var r = await supabase.from('conversations').select('*').order('updated_at', { ascending: false });
      var list = r.data || [];
      setConvos(list);
      var target = null;
      for (var i = 0; i < list.length; i++) { if (String(list[i].id) === String(d.conversation_id)) { target = list[i]; break; } }
      if (target) {
        setCurrentConvo(target);
        loadMessages(target.id);
        setIsExtended(target.extended || false);
      }
      setShowSidebar(false);
    } catch(e) { console.log('silas enter error', e); }
  };

  var exitSilas = function() {
    var target = null;
    for (var i = 0; i < convos.length; i++) { if (convos[i].room !== 'silas' && convos[i].room !== 'lounge') { target = convos[i]; break; } }
    if (target) { setCurrentConvo(target); loadMessages(target.id); setIsExtended(target.extended || false); }
  };

  // 消息去重（同一id只保留第一条）
  var dedupeMessages = function(msgs) {
    var seen = {};
    return msgs.filter(function(m) {
      var key = m.id;
      if (!key || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  };

  // [chat-delta] 发完消息 / 等回复时不再整本重拉：只拉比手上最新那条更新的 + 最近 30 条（盖住流式分段、tool_calls 收尾、撤回这些改过的旧条），合并进现有列表
  var mergeRows = function(prev, rows) {
    var byId = {}; var out = [];
    prev.forEach(function(m) {
      var id = m && m.id;
      if (typeof id === 'string' && (id.indexOf('tmp-') === 0 || id.indexOf('err-') === 0)) return;
      byId[id] = out.length; out.push(m);
    });
    rows.forEach(function(r) {
      if (!r || r.id == null) return;
      if (byId[r.id] !== undefined) out[byId[r.id]] = r;
      else { byId[r.id] = out.length; out.push(r); }
    });
    out.sort(function(a, b) { return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0; });
    return out;
  };
  var refreshRecent = async function(convoId) {
    try {
      var cur = messagesRef.current || [];
      var since = null;
      for (var i = cur.length - 1; i >= 0; i--) {
        var m = cur[i]; var id = m && m.id;
        if (typeof id === 'string' && (id.indexOf('tmp-') === 0 || id.indexOf('err-') === 0 || id.indexOf('pending-') === 0)) continue;
        if (m.created_at && (!since || m.created_at > since)) since = m.created_at;
      }
      var tail = await supabase.from('messages').select('*')
        .eq('conversation_id', convoId).is('edit_branch_id', null)
        .order('created_at', { ascending: false }).limit(30);
      var rows = (tail.data || []).slice().reverse();
      if (since && rows.length === 30 && rows[0].created_at > since) {   // 新的超过 30 条，把 since 之后的也补上
        var more = await supabase.from('messages').select('*')
          .eq('conversation_id', convoId).is('edit_branch_id', null)
          .gt('created_at', since).order('created_at', { ascending: true }).limit(500);
        rows = (more.data || []).concat(rows);
      }
      var merged = dedupeMessages(mergeRows(messagesRef.current || [], rows));
      setMessages(merged);
      return merged;
    } catch (e) { console.log('[chat-delta] 增量刷新失败:', e); return null; }
  };

  var loadMessages = async function(convoId) {
    setLoading(true);
    // [chat-cache] 先读本地：有、而且不超过 24 小时，就直接上屏，然后只拉新的和最近 30 条；没有才整本拉
    var loadedMsgs = null;
    try {
      var cc = await __cc.get(convoId);
      if (cc && cc.msgs && cc.msgs.length && Date.now() - (cc.ts || 0) < 24 * 3600000) {
        loadedMsgs = dedupeMessages(cc.msgs);
        setMessages(loadedMsgs);
        messagesRef.current = loadedMsgs;
        setLoading(false);
        var merged = await refreshRecent(convoId);
        if (merged) loadedMsgs = merged;
      }
    } catch (e) { loadedMsgs = null; }
    if (!loadedMsgs) {
      var r = await supabase.from('messages').select('*').eq('conversation_id', convoId).is('edit_branch_id', null).order('created_at', { ascending: true }).limit(5000);
      loadedMsgs = dedupeMessages(r.data || []);
      setMessages(loadedMsgs);
      __cc.put(convoId, loadedMsgs);
    }

    // 加载编辑分支信息
    var { data: branchData } = await supabase.from('messages')
      .select('edit_branch_id')
      .eq('conversation_id', convoId)
      .not('edit_branch_id', 'is', null);
    var bmap = {};
    (branchData || []).forEach(function(m) {
      var bid = m.edit_branch_id;
      if (!bid) return;
      var parts = bid.split('_');
      if (parts.length >= 3 && parts[0] === 'eb') {
        var mid = parts[1];
        if (!bmap[mid]) bmap[mid] = [];
        if (bmap[mid].indexOf(bid) === -1) bmap[mid].push(bid);
      }
    });
    setEditBranches(bmap);

    setLoading(false);

    // 检查是否有漏掉的回复（最后一条是用户消息 = 可能还在等回复）
    if (loadedMsgs.length > 0 && loadedMsgs[loadedMsgs.length - 1].sender === 'zhizhi' && !sendingRef.current) {
      pollForReply(convoId);
    }
  };

  var pollForReply = function(convoId) {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    var count = 0;
    pollTimerRef.current = setInterval(async function() {
      count++;
      if (count > 10) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; return; }
      try {
        var freshMsgs = await refreshRecent(convoId);   // [chat-delta] 只拉新的，不整本搬
        if (!freshMsgs) return;
        var lastMsg = freshMsgs.length > 0 ? freshMsgs[freshMsgs.length - 1] : null;
        if (lastMsg && lastMsg.sender === 'aiden') {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } catch (e) { console.log('[pollForReply] 等回复轮询失败:', e); }
    }, 3000);
  };

  var switchConvo = function(convo) {
    setCurrentConvo(convo);
    loadMessages(convo.id);
    setShowSidebar(false);
    setExpandedThinking({});
    setEditingId(null);
    setGenSelection({});
    setIsExtended(convo.extended || false);
  };

  var newConvo = async function() {
    var inSilas = currentConvo && currentConvo.room === 'silas';
    var r = await supabase.from('conversations').insert([inSilas ? { title: 'Silas', room: 'silas' } : { title: '\u65B0\u7A97\u53E3' }]).select();
    if (r.data) {
      setConvos(function(p) { return [r.data[0]].concat(p); });
      setCurrentConvo(r.data[0]);
      setMessages([]);
      setExpandedThinking({});
      setEditingId(null);
      setGenSelection({});
    }
  };

  var startRename = function(convo) {
    setRenaming(convo.id);
    setRenameText(convo.title);
  };

  var doRename = async function() {
    if (!renameText.trim()) return;
    await supabase.from('conversations').update({ title: renameText }).eq('id', renaming);
    setConvos(function(p) { return p.map(function(c) { return c.id === renaming ? Object.assign({}, c, { title: renameText }) : c; }); });
    if (currentConvo && currentConvo.id === renaming) { setCurrentConvo(Object.assign({}, currentConvo, { title: renameText })); }
    setRenaming(null);
  };

  var startDelete = function(id) { setDeleting(id); setDeleteStep(1); };

  var confirmDelete = async function() {
    if (deleteStep === 1) { setDeleteStep(2); return; }
    if (deleteStep === 2) { setDeleteStep(3); return; }
    if (deleteStep === 3) {
      try {
        var resp = await fetch('/api/conversations/' + deleting, { method: 'DELETE' });
        var data = await resp.json();
        if (!resp.ok || data.error) {
          alert('删除失败: ' + (data.error || '未知错误'));
          setDeleting(null);
          setDeleteStep(0);
          return;
        }
        setConvos(function(p) { return p.filter(function(c) { return c.id !== deleting; }); });
        if (currentConvo && currentConvo.id === deleting) {
          setCurrentConvo(null);
          setMessages([]);
        }
      } catch(e) {
        alert('删除失败: ' + e.message);
      }
      setDeleting(null);
      setDeleteStep(0);
    }
  };

  var cancelDelete = function() { setDeleting(null); setDeleteStep(0); };

  var handleImageSelect = function(e) {
    var files = Array.from(e.target.files);
    if (files.length === 0) return;
    var splitOn = localStorage.getItem('split_send') === 'true';
    files.forEach(function(file) {
      var reader = new FileReader();
      reader.onload = function(ev) {
        if (splitOn) {
          // 分条模式：积攒到pending，带预览和文件对象
          setPendingMsgs(function(prev) {
            return prev.concat([{ id: 'pending-' + Date.now() + '-' + Math.random(), text: '[图片]', created_at: new Date().toISOString(), preview_url: ev.target.result, image_file: file }]);
          });
        } else {
          setImageFiles(function(prev) { return prev.concat([file]); });
          setImagePreviews(function(prev) { return prev.concat([ev.target.result]); });
        }
      };
      reader.readAsDataURL(file);
    });
    setShowMenu(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  var handleFileSelect = function(e) {
    var files = Array.from(e.target.files);
    if (files.length === 0) return;
    var splitOn = localStorage.getItem('split_send') === 'true';
    files.forEach(function(file) {
      if (splitOn) {
        setPendingMsgs(function(prev) {
          return prev.concat([{ id: 'pending-' + Date.now() + '-' + Math.random(), text: '[文件] ' + file.name, created_at: new Date().toISOString(), doc_file: file, doc_name: file.name }]);
        });
      } else {
        setDocFiles(function(prev) { return prev.concat([file]); });
        setDocNames(function(prev) { return prev.concat([file.name]); });
      }
    });
    setShowMenu(false);
    if (fileDocInputRef.current) fileDocInputRef.current.value = '';
  };

  var removeImage = function(idx) {
    setImageFiles(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
    setImagePreviews(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  };

  var removeDoc = function(idx) {
    setDocFiles(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
    setDocNames(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  };

  // 相册统一入口：图片视频分流，混选各归各位（视频一次只收一个）
  var handleMediaSelect = function(e) {
    var all = Array.from(e.target.files);
    if (all.length === 0) return;
    var vids = all.filter(function(f) { return (f.type || '').indexOf('video/') === 0; });
    var imgs = all.filter(function(f) { return (f.type || '').indexOf('video/') !== 0; });
    if (vids.length > 1) { alert('\u89c6\u9891\u4e00\u6b21\u53ea\u80fd\u53d1\u4e00\u4e2a\uff0c\u5148\u53d6\u7b2c\u4e00\u4e2a'); vids = vids.slice(0, 1); }
    if (vids.length === 1) {
      var vf0 = vids[0];
      if (vf0.size > 120 * 1024 * 1024) {
        alert('\u89c6\u9891\u592a\u5927\u4e86\uff08' + Math.round(vf0.size / 1024 / 1024) + 'MB\uff09\uff0c120MB\u4ee5\u5185\u7684\u624d\u884c');
      } else {
        setVideoFile(vf0);
        setVideoPreviewName(vf0.name + '\uff08' + Math.round(vf0.size / 1024 / 1024) + 'MB\uff09');
      }
    }
    if (imgs.length > 0) {
      handleImageSelect({ target: { files: imgs } });
    }
    setShowMenu(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };


  var removeVideo = function() { setVideoFile(null); setVideoPreviewName(''); setVideoStatus(''); };

  var uploadImage = async function(file) {
    if (typeof file === 'string') return file;   // 图库带来的已经是 URL
    var ext = file.name.split('.').pop();
    var fileName = 'chat/' + Date.now() + '-' + Math.random().toString(36).substring(2, 8) + '.' + ext;
    var result = await supabase.storage.from('images').upload(fileName, file);
    if (result.error) { console.error('Upload error:', result.error); return null; }
    var urlResult = supabase.storage.from('images').getPublicUrl(fileName);
    return urlResult.data.publicUrl;
  };

  var uploadDoc = async function(file) {
    var ext = file.name.split('.').pop();
    var fileName = 'files/' + Date.now() + '-' + Math.random().toString(36).substring(2, 8) + '.' + ext;
    var result = await supabase.storage.from('images').upload(fileName, file);
    if (result.error) { console.error('File upload error:', result.error); return null; }
    var urlResult = supabase.storage.from('images').getPublicUrl(fileName);
    return urlResult.data.publicUrl;
  };

  var getFileIcon = function(name) {
    if (!name) return '\u{1F4CE}';
    var ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '\u{1F4D5}';
    if (ext === 'doc' || ext === 'docx') return '\u{1F4DD}';
    if (ext === 'txt') return '\u{1F4C4}';
    if (ext === 'csv' || ext === 'xlsx' || ext === 'xls') return '\u{1F4CA}';
    return '\u{1F4CE}';
  };

  var toggleThinking = function(msgId) {
    setExpandedThinking(function(prev) {
      var next = Object.assign({}, prev);
      next[msgId] = !next[msgId];
      return next;
    });
  };

  var formatToolCalls = function(toolCallsStr) {
    if (!toolCallsStr) return null;
    try {
      var calls = JSON.parse(toolCallsStr);
      var icons = { read_file: '\u{1F4D6}', write_file: '\u{1F4DD}', list_files: '\u{1F4C2}', run_command: '\u26A1', read_tide: '\u{1F30A}', save_to_knowledge: '\u{1F9E0}' };
      return calls.map(function(c) {
        var icon = icons[c.name] || '\u{1F527}';
        var detail = '';
        if (c.input) {
          if (c.input.path) detail = c.input.path;
          else if (c.input.command) detail = c.input.command;
          else if (c.input.limit) detail = '\u6700\u8FD1' + c.input.limit + '\u6761';
        }
        return { icon: icon, name: c.name, detail: detail };
      });
    } catch (e) { return null; }
  };

  // 复制消息文本（带反馈）
  var copyText = function(msgId, text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      try { navigator.clipboard.writeText(text); } catch (e2) {}
    }
    setCopiedId(msgId);
    setTimeout(function() { setCopiedId(null); }, 1500);
  };

  // 复制代码块
  var [copiedCode, setCopiedCode] = useState(null);
  var copyCode = function(code, idx) {
    try {
      var ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      try { navigator.clipboard.writeText(code); } catch (e2) {}
    }
    setCopiedCode(idx);
    setTimeout(function() { setCopiedCode(null); }, 1500);
  };

  // 渲染消息文本（代码块高亮+复制按钮）
  var [expandedListenMsgs, setExpandedListenMsgs] = useState({});

  var renderMessageText = function(text, msgId) {
    if (!text) return null;
    // 【听歌】/【点歌】消息 → 歌曲卡片
    var isListenMsg = text.indexOf('【听歌】') === 0;
    var isDianMsg = text.indexOf('【点歌】') === 0;
    if (isListenMsg || isDianMsg) {
      var headerEnd = text.indexOf('\n');
      var header = headerEnd > 0 ? text.substring(4, headerEnd) : text.substring(4);
      var dashIdx = header.lastIndexOf(' — ');
      var lsSongName = dashIdx > 0 ? header.substring(0, dashIdx) : header;
      var lsSinger = dashIdx > 0 ? header.substring(dashIdx + 3) : '';
      var coverMatch = text.match(/封面：(https?:\/\/\S+)/);
      var lsCover = coverMatch ? coverMatch[1] : null;
      // 歌词之后的用户附言
      var lyricsIdx = text.indexOf('歌词：\n');
      var tailText = '';
      var lyricsBody = '';
      if (lyricsIdx >= 0) {
        var afterLyrics = text.substring(lyricsIdx + 4);
        var tailSplit = afterLyrics.lastIndexOf('\n\n');
        if (tailSplit > 0) {
          lyricsBody = afterLyrics.substring(0, tailSplit);
          tailText = afterLyrics.substring(tailSplit + 2);
        } else {
          lyricsBody = afterLyrics;
        }
      }
      var isExpanded = !!expandedListenMsgs[msgId];
      var playFromCard = function() {
        if (!musicPlayer) return;
        fetch('/api/music/search', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keyword: lsSongName + ' ' + lsSinger, num: 1 }),
        }).then(function(r) { return r.json(); }).then(function(d) {
          if (d.songs && d.songs[0]) {
            var sv = d.songs[0];
            musicPlayer.playSong({ mid: sv.mid, song_name: sv.name, singer: sv.singer, album_mid: sv.album_mid || '' });
          }
        }).catch(function() {});
      };
      return React.createElement('div', { className: 'listen-share-card' },
        React.createElement('div', { className: 'listen-share-main', onClick: playFromCard, style: { cursor: 'pointer' } },
          lsCover
            ? React.createElement('img', { className: 'listen-share-cover', src: lsCover, alt: '' })
            : React.createElement('div', { className: 'listen-share-cover listen-share-cover-empty' },
                React.createElement('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' },
                  React.createElement('path', { d: 'M9 18V5l12-2v13' }),
                  React.createElement('circle', { cx: 6, cy: 18, r: 3 }),
                  React.createElement('circle', { cx: 18, cy: 16, r: 3 }))),
          React.createElement('div', { className: 'listen-share-info' },
            React.createElement('div', { className: 'listen-share-name' }, lsSongName),
            React.createElement('div', { className: 'listen-share-singer' }, lsSinger),
            React.createElement('div', { className: 'listen-share-tag' }, isDianMsg ? '\u54E5\u54E5\u70B9\u7ED9\u4F60\u7684' : '\u5206\u4EAB\u7ED9\u54E5\u54E5\u542C')
          )
        ),
        lyricsBody && React.createElement('button', {
          className: 'listen-share-toggle',
          onClick: function(e) {
            e.stopPropagation();
            setExpandedListenMsgs(function(prev) {
              var next = Object.assign({}, prev);
              next[msgId] = !prev[msgId];
              return next;
            });
          }
        }, isExpanded ? '\u6536\u8D77\u6B4C\u8BCD' : '\u5C55\u5F00\u6B4C\u8BCD'),
        isExpanded && lyricsBody && React.createElement('div', { className: 'listen-share-lyrics' }, lyricsBody),
        tailText && React.createElement('div', { className: 'listen-share-tail' }, tailText)
      );
    }
    var parts = text.split(/(```[\s\S]*?```)/g);
    var codeIdx = 0;
    return parts.map(function(part, i) {
      var codeMatch = part.match(/^```(\w*)\n?([\s\S]*?)```$/);
      if (codeMatch) {
        var lang = codeMatch[1] || '';
        var code = codeMatch[2].replace(/\n$/, '');
        var thisIdx = msgId + '-' + codeIdx;
        codeIdx++;
        return React.createElement('div', { key: i, style: { position: 'relative', margin: '8px 0', borderRadius: 8, overflow: 'hidden', background: 'var(--bg)', border: '1px solid var(--border)' } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px', background: 'var(--border)', fontSize: 11, color: 'var(--text3)' } },
            React.createElement('span', null, lang || 'code'),
            React.createElement('button', {
              onClick: function(e) { e.stopPropagation(); copyCode(code, thisIdx); },
              style: { background: 'none', border: 'none', color: copiedCode === thisIdx ? 'var(--accent)' : 'var(--text3)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-sans)', padding: '2px 6px' }
            }, copiedCode === thisIdx ? '✓ 已复制' : '⎘ 复制')
          ),
          React.createElement('pre', { style: { margin: 0, padding: '10px 12px', overflow: 'auto', fontSize: 12, lineHeight: 1.5, fontFamily: "'SF Mono',Menlo,monospace", color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' } },
            React.createElement('code', null, code)
          )
        );
      }
      if (!part.trim()) return null;
      return React.createElement('span', { key: i, style: { whiteSpace: 'pre-wrap' } }, part);
    });
  };

  // 存入知识库
  var [kbSaved, setKbSaved] = useState(null);
  var saveToKnowledge = async function(msgId, text) {
    var title = window.prompt('知识库标题（如：窗口摘要 6.16）');
    if (!title) return;
    try {
      await fetch('/api/knowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title, content: text, priority: 8 })
      });
      setKbSaved(msgId);
      setTimeout(function() { setKbSaved(null); }, 2000);
    } catch (e) { alert('保存失败: ' + e.message); }
  };

  // ========== 星标系统 (Phase 3) ==========
  var toggleStar = function(msg) {
    if (msg.starred) {
      // 取消星标
      supabase.from('messages').update({ starred: false, star_label: null }).eq('id', msg.id).then(function() {
        setMessages(function(prev) { return prev.map(function(m) { return m.id === msg.id ? Object.assign({}, m, { starred: false, star_label: null }) : m; }); });
      });
    } else {
      // 弹出标签输入
      setStarringId(msg.id);
      setStarLabel('');
    }
  };

  var confirmStar = async function() {
    if (!starringId) return;
    var label = starLabel.trim() || null;
    await supabase.from('messages').update({ starred: true, star_label: label }).eq('id', starringId);
    setMessages(function(prev) { return prev.map(function(m) { return m.id === starringId ? Object.assign({}, m, { starred: true, star_label: label }) : m; }); });
    setStarringId(null);
    setStarLabel('');
  };

  var loadStarredMessages = async function() {
    try {
      var { data } = await supabase.from('messages')
        .select('id, text, sender, created_at, star_label, conversation_id, image_url')
        .eq('starred', true)
        .order('created_at', { ascending: false })
        .limit(200);
      setStarredMsgs(data || []);
    } catch(e) { console.error('Load starred error:', e); }
  };

  var loadVoiceCollection = async function() {
    try {
      var { data } = await supabase.from('messages')
        .select('id, text, sender, created_at, conversation_id, tts_url')
        .not('tts_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(200);
      setVoiceCollection(data || []);
    } catch(e) { console.error('Load voice collection error:', e); }
  };

  var deleteVoiceItem = async function(msgId) {
    await supabase.from('messages').update({ tts_url: null }).eq('id', msgId);
    setVoiceCollection(function(prev) { return prev.filter(function(v) { return v.id !== msgId; }); });
    // 清内存缓存
    if (ttsCacheRef.current[msgId]) {
      delete ttsCacheRef.current[msgId];
    }
  };

  // ========== 聊天记录搜索 (Phase 4) ==========
  var doChatSearch = async function(query) {
    if (!query || !query.trim()) { setChatSearchResults([]); return; }
    setChatSearching(true);
    try {
      var q = query.trim();
      var builder = supabase.from('messages')
        .select('id, text, sender, created_at, conversation_id, starred, star_label')
        .neq('is_active', false)
        .ilike('text', '%' + q + '%')
        .order('created_at', { ascending: false })
        .limit(100);
      if (searchDateFrom) builder = builder.gte('created_at', new Date(searchDateFrom + 'T00:00:00+08:00').toISOString());
      if (searchDateTo) builder = builder.lte('created_at', new Date(searchDateTo + 'T23:59:59+08:00').toISOString());
      var { data } = await builder;
      setChatSearchResults(data || []);
    } catch(e) { console.error('Chat search error:', e); }
    setChatSearching(false);
  };

  // ========== 延长对话 (Phase 2 规则D) ==========
  var toggleExtend = async function() {
    if (!currentConvo) return;
    var newState = !isExtended;
    try {
      await fetch('/api/' + (newState ? 'extend-conversation' : 'end-extend'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: currentConvo.id })
      });
      setIsExtended(newState);
    } catch(e) { console.error('Extend toggle error:', e); }
  };

  // ========== 表情包系统 ==========
  var loadStickers = async function() {
    try {
      var resp = await fetch('/api/stickers');
      var data = await resp.json();
      if (Array.isArray(data)) setStickerList(data);
    } catch (e) { console.error('Load stickers error:', e); }
  };

  var uploadSticker = async function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      var formData = new FormData();
      formData.append('image', file);
      formData.append('name', file.name);
      var resp = await fetch('/api/stickers/upload', { method: 'POST', body: formData });
      var data = await resp.json();
      if (data.url) {
        setStickerList(function(prev) { return [data].concat(prev); });
      }
    } catch (e) { console.error('Upload sticker error:', e); }
    if (stickerFileRef.current) stickerFileRef.current.value = '';
  };

  var sendSticker = async function(sticker) {
    var stickerUrl = typeof sticker === 'string' ? sticker : sticker.url;
    var stickerDesc = (typeof sticker === 'object' && sticker.description) ? sticker.description : '';
    var stickerName = (typeof sticker === 'object' && sticker.name) ? sticker.name : '';
    // text带上描述，让AI能认出知枝发的是哪张
    var stickerText = stickerDesc ? '[表情包：' + stickerDesc + ']' : '[sticker]';
    if (!currentConvo || sending || sendingRef.current) return;
    setShowStickerPanel(false);
    // 分条模式：积攒表情包，不立即发送
    var splitOn = localStorage.getItem('split_send') === 'true';
    if (splitOn) {
      setPendingMsgs(function(prev) {
        return prev.concat([{ id: 'pending-' + Date.now(), text: stickerText, created_at: new Date().toISOString(), image_url: stickerUrl }]);
      });
      return;
    }
    var now = new Date().toISOString();
    var tmp = { id: 'tmp-' + Date.now(), text: stickerText, sender: 'zhizhi', created_at: now, conversation_id: currentConvo.id, image_url: stickerUrl };
    setMessages(function(p) { return p.concat([tmp]); });
    var r = await supabase.from('messages').insert([{ text: stickerText, sender: 'zhizhi', conversation_id: currentConvo.id, image_url: stickerUrl }]).select();
    if (r.data && r.data[0]) {
      setMessages(function(p) { return p.map(function(m) { return m.id === tmp.id ? r.data[0] : m; }); });
    }
    await supabase.from('conversations').update({ updated_at: now }).eq('id', currentConvo.id);
  };

  var deleteSticker = async function(stickerId) {
    try {
      await fetch('/api/stickers/' + stickerId, { method: 'DELETE' });
      setStickerList(function(prev) { return prev.filter(function(s) { return s.id !== stickerId; }); });
    } catch (e) { console.error('Delete sticker error:', e); }
    setDeleteConfirmSticker(null);
  };

  var openStickerPanel = function() {
    setShowMenu(false);
    setShowStickerPanel(true);
    loadStickers();
  };

  // 重试：标记旧消息inactive，重新生成
  var retryMessage = async function(aidenMsgId) {
    if (sending || sendingRef.current || !currentConvo) return;
    sendingRef.current = true;
    setSending(true);

    // 找到前面最后一条zhizhi的消息
    var aidenIdx = messages.findIndex(function(m) { return m.id === aidenMsgId; });
    var lastUserMsg = null;
    for (var i = aidenIdx - 1; i >= 0; i--) {
      if (messages[i].sender === 'zhizhi') { lastUserMsg = messages[i]; break; }
    }
    if (!lastUserMsg) { sendingRef.current = false; setSending(false); return; }

    // 标记旧消息为inactive
    if (typeof aidenMsgId === 'number' || (typeof aidenMsgId === 'string' && !aidenMsgId.startsWith('err-'))) {
      await supabase.from('messages').update({ is_active: false }).eq('id', aidenMsgId);
      setMessages(function(p) { return p.map(function(m) { return m.id === aidenMsgId ? Object.assign({}, m, { is_active: false }) : m; }); });
    }

    // 重新调用后端
    try {
      var resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: currentConvo.id,
          message: lastUserMsg.text || '',
          image_url: lastUserMsg.image_url || null,
          file_url: lastUserMsg.file_url || null,
          video_frames: lastUserMsg.video_frames || null,
          file_name: lastUserMsg.file_name || null
        })
      });
      var data = await resp.json();
      if (data.reply) {
        await refreshRecent(currentConvo.id);   // [chat-delta]
      } else if (data.error) {
        var errMsg = { id: 'err-' + Date.now(), text: '[\u91CD\u8BD5\u5931\u8D25: ' + data.error + ']', sender: 'aiden', created_at: new Date().toISOString(), conversation_id: currentConvo.id };
        setMessages(function(p) { return p.concat([errMsg]); });
      }
    } catch (err) {
      var errMsg2 = { id: 'err-' + Date.now(), text: '[\u8FDE\u63A5\u540E\u7AEF\u5931\u8D25]', sender: 'aiden', created_at: new Date().toISOString(), conversation_id: currentConvo.id };
      setMessages(function(p) { return p.concat([errMsg2]); });
    }
    sendingRef.current = false;
    setSending(false);
  };

  // 消息分组：连续的aiden消息合并为一组（用于多次生成切换）
  var buildDisplayGroups = function() {
    var groups = [];
    for (var i = 0; i < messages.length; i++) {
      var msg = messages[i];
      if (msg.sender === 'aiden') {
        var lastGroup = groups.length > 0 ? groups[groups.length - 1] : null;
        var isSticker = msg.text === '[sticker]' || (msg.text && msg.text.startsWith('[表情包'));
        var lastIsSticker = lastGroup && lastGroup.msgs.length > 0 && (lastGroup.msgs[lastGroup.msgs.length - 1].text === '[sticker]' || (lastGroup.msgs[lastGroup.msgs.length - 1].text && lastGroup.msgs[lastGroup.msgs.length - 1].text.startsWith('[表情包')));
        var isRetry = msg.is_active === false || (lastGroup && lastGroup.msgs.some(function(m) { return m.is_active === false; }));
        // sticker永远独立
        if (isSticker || lastIsSticker) {
          groups.push({ sender: 'aiden', msgs: [msg], key: 'ag-' + msg.id });
        // retry版本合并（用于切换器）
        } else if (lastGroup && lastGroup.sender === 'aiden' && isRetry) {
          lastGroup.msgs.push(msg);
        // 无气泡模式：合并连续aiden文字消息成一段
        } else if (!bubbleMode && lastGroup && lastGroup.sender === 'aiden') {
          lastGroup.msgs.push(msg);
        // 气泡模式：每条独立
        } else {
          groups.push({ sender: 'aiden', msgs: [msg], key: 'ag-' + msg.id });
        }
      } else {
        groups.push({ sender: 'zhizhi', msgs: [msg], key: 'zg-' + msg.id });
      }
    }
    return groups;
  };

  var getGenIndex = function(groupKey, total) {
    if (genSelection[groupKey] !== undefined) return genSelection[groupKey];
    return total - 1;
  };

  var setGenIndex = function(groupKey, idx) {
    setGenSelection(function(prev) { var next = Object.assign({}, prev); next[groupKey] = idx; return next; });
  };

  var isLastAidenGroup = function(groupKey, groups) {
    for (var i = groups.length - 1; i >= 0; i--) {
      if (groups[i].sender === 'aiden') return groups[i].key === groupKey;
    }
    return false;
  };

  // 编辑消息：开始编辑
  var startEdit = function(msg) {
    setEditingId(msg.id);
    setEditText(msg.text || '');
  };

  // 编辑消息：取消
  var cancelEdit = function() {
    setEditingId(null);
    setEditText('');
  };

  // 编辑消息：保存
  var saveEdit = async function(msgId) {
    if (!editText.trim()) return;
    await supabase.from('messages').update({ text: editText }).eq('id', msgId);
    setMessages(function(p) {
      return p.map(function(m) { return m.id === msgId ? Object.assign({}, m, { text: editText }) : m; });
    });
    setEditingId(null);
    setEditText('');
  };

  // 编辑并重新生成：保存旧分支 + 编辑 + 重新生成
  var saveEditAndRegenerate = async function(msgId) {
    if (!editText.trim() || sending || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);

    var editIdx = messages.findIndex(function(m) { return m.id === msgId; });
    var originalText = messages[editIdx].text;
    var branchId = 'eb_' + msgId + '_' + Date.now();

    // 创建快照消息（记录编辑前的用户消息文本，用于分支切换时恢复）
    await supabase.from('messages').insert([{
      text: originalText,
      sender: messages[editIdx].sender,
      conversation_id: currentConvo.id,
      is_active: false,
      edit_branch_id: branchId,
      created_at: messages[editIdx].created_at
    }]);

    // 后续消息标记inactive + edit_branch_id
    for (var i = editIdx + 1; i < messages.length; i++) {
      var deactId = messages[i].id;
      if (typeof deactId === 'number' || (typeof deactId === 'string' && !deactId.startsWith('err-') && !deactId.startsWith('tmp-'))) {
        await supabase.from('messages').update({ is_active: false, edit_branch_id: branchId }).eq('id', deactId);
      }
    }

    // 保存编辑
    await supabase.from('messages').update({ text: editText }).eq('id', msgId);

    // 更新前端
    setMessages(function(p) {
      var updated = p.slice(0, editIdx + 1);
      updated[editIdx] = Object.assign({}, updated[editIdx], { text: editText });
      return updated;
    });
    setEditingId(null);
    setEditText('');

    // 重新生成
    var editedMsg = messages[editIdx];
    try {
      var resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: currentConvo.id,
          message: editText,
          image_url: editedMsg.image_url || null,
          file_url: editedMsg.file_url || null,
          video_frames: editedMsg.video_frames || null,
          file_name: editedMsg.file_name || null
        })
      });
      var data = await resp.json();
      if (data.reply) {
        await refreshRecent(currentConvo.id);   // [chat-delta]
      } else if (data.error) {
        var errMsg = { id: 'err-' + Date.now(), text: '[\u56DE\u590D\u5931\u8D25: ' + data.error + ']', sender: 'aiden', created_at: new Date().toISOString(), conversation_id: currentConvo.id };
        setMessages(function(p) { return p.concat([errMsg]); });
      }
    } catch (err) {
      var errMsg2 = { id: 'err-' + Date.now(), text: '[\u8FDE\u63A5\u540E\u7AEF\u5931\u8D25]', sender: 'aiden', created_at: new Date().toISOString(), conversation_id: currentConvo.id };
      setMessages(function(p) { return p.concat([errMsg2]); });
    }

    // 重新加载分支信息
    if (currentConvo) {
      var { data: branchData } = await supabase.from('messages')
        .select('edit_branch_id')
        .eq('conversation_id', currentConvo.id)
        .not('edit_branch_id', 'is', null);
      var bmap = {};
      (branchData || []).forEach(function(m) {
        var bid = m.edit_branch_id;
        if (!bid) return;
        var parts = bid.split('_');
        if (parts.length >= 3 && parts[0] === 'eb') {
          var mid = parts[1];
          if (!bmap[mid]) bmap[mid] = [];
          if (bmap[mid].indexOf(bid) === -1) bmap[mid].push(bid);
        }
      });
      setEditBranches(bmap);
    }

    sendingRef.current = false;
    setSending(false);
  };

  // 切换编辑分支
  var switchEditBranch = async function(msgId, branchId) {
    if (sending || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      var resp = await fetch('/api/edit-branch/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ msg_id: msgId, branch_id: branchId, conversation_id: currentConvo.id })
      });
      var data = await resp.json();
      if (data.ok && currentConvo) {
        await loadMessages(currentConvo.id);
      }
    } catch (err) {
      console.error('Switch branch error:', err);
    }
    sendingRef.current = false;
    setSending(false);
  };

  // ========== TTS 播放函数 ==========
  var playTts = async function(msgId, text) {
    if (ttsPlayingId === msgId && ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
      setTtsPlayingId(null);
      return;
    }
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
      setTtsPlayingId(null);
    }
    // 先查内存缓存
    if (ttsCacheRef.current[msgId]) {
      var cached = new Audio(ttsCacheRef.current[msgId]);
      ttsAudioRef.current = cached;
      setTtsPlayingId(msgId);
      cached.play();
      cached.addEventListener('ended', function() { setTtsPlayingId(null); ttsAudioRef.current = null; });
      return;
    }
    // 再查数据库里有没有已生成的tts_url
    var msg = messages.find(function(m) { return m.id === msgId; });
    if (msg && msg.tts_url) {
      ttsCacheRef.current[msgId] = msg.tts_url;
      var fromDb = new Audio(msg.tts_url);
      ttsAudioRef.current = fromDb;
      setTtsPlayingId(msgId);
      fromDb.play();
      fromDb.addEventListener('ended', function() { setTtsPlayingId(null); ttsAudioRef.current = null; });
      return;
    }
    // 都没有，调ElevenLabs生成 → 上传Storage → 存数据库
    setTtsLoadingId(msgId);
    try {
      var resp = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text })
      });
      if (!resp.ok) throw new Error('TTS failed');
      var blob = await resp.blob();
      // 上传到Supabase Storage
      var storagePath = 'tts/' + Date.now() + '-' + Math.random().toString(36).substring(2, 8) + '.mp3';
      var uploadResult = await supabase.storage.from('images').upload(storagePath, blob, { contentType: 'audio/mpeg', upsert: false });
      var ttsUrl = null;
      if (!uploadResult.error) {
        var urlResult = supabase.storage.from('images').getPublicUrl(storagePath);
        ttsUrl = urlResult.data.publicUrl;
        // 存到messages表
        await supabase.from('messages').update({ tts_url: ttsUrl }).eq('id', msgId);
        // 更新前端messages state
        setMessages(function(prev) { return prev.map(function(m) { return m.id === msgId ? Object.assign({}, m, { tts_url: ttsUrl }) : m; }); });
      }
      var playUrl = ttsUrl || URL.createObjectURL(blob);
      ttsCacheRef.current[msgId] = playUrl;
      var audio = new Audio(playUrl);
      ttsAudioRef.current = audio;
      setTtsLoadingId(null);
      setTtsPlayingId(msgId);
      audio.play();
      audio.addEventListener('ended', function() { setTtsPlayingId(null); ttsAudioRef.current = null; });
    } catch(e) {
      console.error('TTS error:', e);
      setTtsLoadingId(null);
    }
  };

  // ========== 语音录制函数 ==========
  var startRecording = async function() {
    if (mediaRecorderRef.current || isRecording) return;
    var stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error('Microphone access denied:', e);
      return;
    }
    var audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    var source = audioCtx.createMediaStreamSource(stream);
    var analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128;
    source.connect(analyser);
    analyserRef.current = analyser;
    audioCtxRef.current = audioCtx;
    audioChunksRef.current = [];
    var mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    var recorder = new MediaRecorder(stream, { mimeType: mimeType });
    recorder.ondataavailable = function(e) { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
    recorder.onstop = function() {
      stream.getTracks().forEach(function(t) { t.stop(); });
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch(e) {} }
      finishRecording();
    };
    recorder.start(100);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    setRecSeconds(0);
    recSecondsRef.current = 0;
    recTimerRef2.current = setInterval(function() {
      recSecondsRef.current += 1;
      setRecSeconds(recSecondsRef.current);
    }, 1000);
    // 动画波形
    var buf = new Uint8Array(analyser.frequencyBinCount);
    var drawWave = function() {
      animFrameRef.current = requestAnimationFrame(drawWave);
      analyser.getByteFrequencyData(buf);
      var bars = [];
      for (var i = 0; i < 32; i++) {
        var idx = Math.floor(i * buf.length / 32);
        var v = buf[idx] / 255;
        bars.push(Math.max(4, Math.round(v * 28)));
      }
      setRecWaveBars(bars);
    };
    drawWave();
  };

  var stopRecording = function() {
    if (!mediaRecorderRef.current) return;
    if (recTimerRef2.current) { clearInterval(recTimerRef2.current); recTimerRef2.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    mediaRecorderRef.current.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecWaveBars(new Array(32).fill(4));
  };

  var cancelRecording = function() {
    // 无条件关掉UI，不管recorder是什么状态
    if (recTimerRef2.current) { clearInterval(recTimerRef2.current); recTimerRef2.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    if (mediaRecorderRef.current) {
      var stream = mediaRecorderRef.current.stream;
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      try { mediaRecorderRef.current.stop(); } catch(e) {}
      if (stream) stream.getTracks().forEach(function(t) { t.stop(); });
    }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch(e) {} }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setIsRecording(false);
    setVoiceUploading(false);
    setRecWaveBars(new Array(32).fill(4));
    setRecSeconds(0);
    recSecondsRef.current = 0;
  };

  var finishRecording = async function() {
    var chunks = audioChunksRef.current;
    if (!chunks || chunks.length === 0) return;
    var duration = recSecondsRef.current;
    var blob = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' });
    audioChunksRef.current = [];
    if (blob.size < 500 || duration < 1) return; // 太短忽略
    setVoiceUploading(true);
    try {
      // 1. 上传到Supabase获取URL
      var ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      var formData = new FormData();
      formData.append('audio', blob, 'voice.' + ext);
      var uploadResp = await fetch('/api/upload-audio', { method: 'POST', body: formData });
      var uploadData = await uploadResp.json();
      if (!uploadData.url) throw new Error('Upload failed');
      var audioUrl = uploadData.url;
      // 2. STT转写
      var sttFormData = new FormData();
      sttFormData.append('audio', blob, 'voice.' + ext);
      var sttResp = await fetch('/api/stt/transcribe', { method: 'POST', body: sttFormData });
      var sttData = await sttResp.json();
      var transcript = sttData.text || '';
      var emotion = sttData.emotion || 'neutral';
      // 3. 发送语音消息
      await sendVoiceMessage(audioUrl, transcript, emotion, duration, blob);
    } catch (e) {
      console.error('Voice message failed:', e);
    }
    setVoiceUploading(false);
  };

  var sendVoiceMessage = async function(audioUrl, transcript, emotion, duration, blob) {
    if (!currentConvo) return;
    var now = new Date().toISOString();
    var voiceText = '\uD83C\uDFA4 ' + (duration < 60 ? duration + 's' : Math.floor(duration/60) + ':' + (duration%60).toString().padStart(2,'0'));
    if (transcript) voiceText += ' | ' + transcript;
    var blobUrl = URL.createObjectURL(blob);
    var tmp = { id: 'tmp-voice-' + Date.now(), text: voiceText, sender: 'zhizhi', created_at: now, conversation_id: currentConvo.id, audio_url: audioUrl, _voice_blob_url: blobUrl, _voice_duration: duration, _voice_transcript: transcript, _voice_emotion: emotion };
    setMessages(function(p) { return p.concat([tmp]); });
    var insertData = { text: voiceText, sender: 'zhizhi', conversation_id: currentConvo.id, audio_url: audioUrl, voice_emotion: emotion };
    var r = await supabase.from('messages').insert([insertData]).select();
    if (r.data && r.data[0]) {
      r.data[0]._voice_blob_url = blobUrl;
      r.data[0]._voice_duration = duration;
      r.data[0]._voice_transcript = transcript;
      r.data[0]._voice_emotion = emotion;
      setMessages(function(p) { return p.map(function(m) { return m.id === tmp.id ? r.data[0] : m; }); });
    }
    await supabase.from('conversations').update({ updated_at: now }).eq('id', currentConvo.id);
    // 调AI回复（传transcript作为message内容）
    if (transcript) {
      sendingRef.current = true;
      setSending(true);
      try {
        var resp = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversation_id: currentConvo.id, message: '[语音消息] ' + transcript + (emotion !== 'neutral' ? ' (情绪: ' + emotion + ')' : '') })
        });
        var data = await resp.json();
        if (data.reply) {
          await refreshRecent(currentConvo.id);   // [chat-delta]
        }
        if(data.bt_commands&&data.bt_commands.length>0){data.bt_commands.forEach(function(cmd){if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.nativeAction){window.webkit.messageHandlers.nativeAction.postMessage(cmd);}});}
        if(data.music_commands&&data.music_commands.length>0){setPendingMusic(data.music_commands[0]);}
        if(data.night_pass){data.night_pass.forEach(function(a){if(a==='show_form')setShowNightPassForm(true);if(a==='approved'){setNightPassApproved(true);setTimeout(function(){setNightPassApproved(false);},3000);}if(a==='rejected'){setNightPassRejected(true);setTimeout(function(){setNightPassRejected(false);},3000);}});}
      } catch (e) { console.error('Voice chat error:', e); }
      sendingRef.current = false;
      setSending(false);
    }
  };

  // fmtVoiceDuration 与 VoiceBar 已移到文件顶部（模块级，防止父级重渲染时整个语音条被重挂载）

  var sendMessage = async function() {
    // 一条条发送模式：有积攒的消息
    var splitOn = localStorage.getItem('split_send') === 'true';
    var allPending = pendingMsgs.slice();
    if (splitOn && input.trim()) {
      allPending.push({ id: 'pending-' + Date.now(), text: input.trim(), created_at: new Date().toISOString() });
    }
    if (splitOn && allPending.length > 0) {
      if (!currentConvo || sending || sendingRef.current) return;
      sendingRef.current = true;
      setSending(true);
      setInput('');
      setPendingMsgs([]);
      // 分条模式也要消费歌词引用和听歌请求（附加到第一条消息）
      if (musicPlayer && musicPlayer.quotedLyric) {
        var qlS = musicPlayer.quotedLyric;
        allPending[0].text = '「' + qlS.text + '」—— ' + qlS.song_name + '\n' + (allPending[0].text || '');
        musicPlayer.setQuotedLyric(null);
      }
      if (musicPlayer && musicPlayer.listenRequest) {
        var lrS = musicPlayer.listenRequest;
        var coverLineS = (lrS.album_mid && lrS.album_mid.indexOf('http') === 0) ? ('封面：' + lrS.album_mid + '\n') : '';
        // 歌卡独立成一条，附言保持原pending条目
        allPending.unshift({
          id: 'pending-listen-' + Date.now(),
          text: '【听歌】' + lrS.song_name + ' — ' + lrS.singer + '\n' + coverLineS + '\n歌词：\n' + lrS.lyricsText,
          created_at: new Date().toISOString(),
        });
        musicPlayer.setListenRequest(null);
      }
      // 把pending消息移到正式消息列表（图片用preview_url临时显示）
      var tmpMsgs = allPending.map(function(p, idx) {
        var msg = { id: 'tmp-' + Date.now() + '-' + idx, text: p.text, sender: 'zhizhi', created_at: new Date(Date.now() + idx * 500).toISOString(), conversation_id: currentConvo.id };
        if (p.image_url) msg.image_url = p.image_url;
        else if (p.preview_url) msg.image_url = p.preview_url;
        return msg;
      });
      setMessages(function(prev) { return prev.concat(tmpMsgs); });
      // 逐条插入数据库（图片和文件先上传）
      var lastImageUrl = null;
      for (var li = 0; li < allPending.length; li++) {
        var insertData = { text: allPending[li].text, sender: 'zhizhi', conversation_id: currentConvo.id };
        if (allPending[li].image_url) {
          insertData.image_url = allPending[li].image_url;
          lastImageUrl = allPending[li].image_url;
        } else if (allPending[li].image_file) {
          var imgUrl = await uploadImage(allPending[li].image_file);
          if (imgUrl) {
            insertData.image_url = imgUrl;
            lastImageUrl = imgUrl;
            allPending[li].image_url = imgUrl;
            // 更新临时消息的图片URL（从preview替换为真实URL）
            var tmpId = tmpMsgs[li].id;
            setMessages(function(prev) {
              return prev.map(function(m) {
                return m.id === tmpId ? Object.assign({}, m, { image_url: imgUrl }) : m;
              });
            });
          }
          // （修复第三代重复：不再用input覆盖'[图片]'占位——打的字已经作为独立一条pending入库，再覆盖会让同一句话存两行）
        }
        if (allPending[li].doc_file) {
          var docUrl = await uploadDoc(allPending[li].doc_file);
          if (docUrl) {
            insertData.file_url = docUrl;
            insertData.file_name = allPending[li].doc_name;
          }
        }
        await supabase.from('messages').insert([insertData]);
      }
      // 用最后一条调API（收集所有图片真实URL和文字）
      var allImages = [];
      var allTexts = [];
      for (var pi = 0; pi < allPending.length; pi++) {
        if (allPending[pi].image_url) allImages.push(allPending[pi].image_url);
        else if (allPending[pi].preview_url) allImages.push(allPending[pi].preview_url);
        if (allPending[pi].text && allPending[pi].text !== '[图片]') allTexts.push(allPending[pi].text);
      }
      var apiImageUrl = allImages.length > 1 ? JSON.stringify(allImages) : (allImages.length === 1 ? allImages[0] : lastImageUrl);
      var apiMessage = allTexts.length > 0 ? allTexts.join('\n') : (allPending[allPending.length - 1].text || '[图片]');
      try {
        var resp = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversation_id: currentConvo.id, message: apiMessage, image_url: apiImageUrl || null })
        });
        var data = await resp.json();
        if (data.reply !== undefined) {
          await refreshRecent(currentConvo.id);   // [chat-delta]
        }
        // 转发蓝牙指令
        if(data.bt_commands&&data.bt_commands.length>0){data.bt_commands.forEach(function(cmd){if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.nativeAction){window.webkit.messageHandlers.nativeAction.postMessage(cmd);}});}
        if(data.music_commands&&data.music_commands.length>0){setPendingMusic(data.music_commands[0]);}
        if(data.night_pass){data.night_pass.forEach(function(a){if(a==='show_form')setShowNightPassForm(true);if(a==='approved'){setNightPassApproved(true);setTimeout(function(){setNightPassApproved(false);},3000);}if(a==='rejected'){setNightPassRejected(true);setTimeout(function(){setNightPassRejected(false);},3000);}});}
      } catch(e) { console.error('Send error:', e); }
      sendingRef.current = false;
      setSending(false);
      return;
    }

    if (input.trim() === '' && imageFiles.length === 0 && docFiles.length === 0 && !videoFile && !(musicPlayer && musicPlayer.quotedLyric) && !(musicPlayer && musicPlayer.listenRequest)) return;
    if (!currentConvo || sending || sendingRef.current) return;
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    sendingRef.current = true;
    var t = input;
    // 附加歌词引用
    if (musicPlayer && musicPlayer.quotedLyric) {
      var ql = musicPlayer.quotedLyric;
      var quotePrefix = '「' + ql.text + '」—— ' + ql.song_name + '\n';
      t = quotePrefix + t;
      musicPlayer.setQuotedLyric(null);
    }
    // AI听歌 —— 歌卡和附言分开发，歌一条、话一条
    var listenExtra = null;
    if (musicPlayer && musicPlayer.listenRequest) {
      var lr = musicPlayer.listenRequest;
      var coverLine = (lr.album_mid && lr.album_mid.indexOf('http') === 0) ? ('封面：' + lr.album_mid + '\n') : '';
      listenExtra = '【听歌】' + lr.song_name + ' — ' + lr.singer + '\n' + coverLine + '\n歌词：\n' + lr.lyricsText;
      musicPlayer.setListenRequest(null);
      if (!t.trim()) t = '哥哥你听听这首歌，写个听后感';
    }
    var now = new Date().toISOString();
    setSending(true);

    var imageUrls = [];
    for (var ii = 0; ii < imageFiles.length; ii++) {
      var iurl = await uploadImage(imageFiles[ii]);
      if (iurl) imageUrls.push(iurl);
    }

    var fileUrls = [];
    var fileNamesArr = [];
    for (var fi = 0; fi < docFiles.length; fi++) {
      var furl = await uploadDoc(docFiles[fi]);
      if (furl) { fileUrls.push(furl); fileNamesArr.push(docNames[fi]); }
    }

    // 视频：原片直传Supabase → 后端抽帧+转播放版
    var videoUrlVal = null, videoFramesVal = null, videoDurationVal = null;
    if (videoFile) {
      setVideoStatus('\u4e0a\u4f20\u89c6\u9891\u4e2d...');
      var vExt = (videoFile.name.split('.').pop() || 'mp4').toLowerCase();
      var vPath = 'videos/raw_' + Date.now() + '-' + Math.random().toString(36).substring(2, 8) + '.' + vExt;
      var vRes = await supabase.storage.from('images').upload(vPath, videoFile, { contentType: videoFile.type || 'video/mp4' });
      if (vRes.error) {
        alert('\u89c6\u9891\u4e0a\u4f20\u5931\u8d25\uff1a' + vRes.error.message);
        setVideoStatus(''); sendingRef.current = false; setSending(false);
        return;
      }
      var vPub = supabase.storage.from('images').getPublicUrl(vPath).data.publicUrl;
      setVideoStatus('\u4ed6\u5728\u770b\u89c6\u9891...\uff08\u62bd\u5e27\u8f6c\u7801\uff0c\u4e8c\u4e09\u5341\u79d2\uff09');
      try {
        var pvResp = await fetch('/api/process-video', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ video_url: vPub }) });
        var pv = await pvResp.json();
        if (!pv.ok) {
          alert('\u89c6\u9891\u5904\u7406\u5931\u8d25\uff1a' + (pv.error || '\u672a\u77e5\u9519\u8bef'));
          setVideoStatus(''); sendingRef.current = false; setSending(false);
          return;
        }
        videoUrlVal = pv.play_url;
        videoFramesVal = JSON.stringify(pv.frames);
        videoDurationVal = pv.duration;
      } catch (vErr) {
        alert('\u89c6\u9891\u5904\u7406\u5931\u8d25\uff0c\u7a0d\u540e\u518d\u8bd5');
        setVideoStatus(''); sendingRef.current = false; setSending(false);
        return;
      }
      setVideoStatus('');
      setVideoFile(null); setVideoPreviewName('');
      if (!t || !t.trim()) t = '[\u89c6\u9891]';
    }

    // 数据库存储：多个用JSON数组，单个用字符串，没有就null
    var imageUrlVal = imageUrls.length > 1 ? JSON.stringify(imageUrls) : (imageUrls.length === 1 ? imageUrls[0] : null);
    var fileUrlVal = fileUrls.length > 1 ? JSON.stringify(fileUrls) : (fileUrls.length === 1 ? fileUrls[0] : null);
    var fileNameVal = fileNamesArr.length > 1 ? JSON.stringify(fileNamesArr) : (fileNamesArr.length === 1 ? fileNamesArr[0] : null);

    // 听歌卡先单独入库一条（歌一条、话一条）
    if (listenExtra) {
      var tmpL = { id: 'tmp-listen-' + Date.now(), text: listenExtra, sender: 'zhizhi', created_at: new Date(Date.now() - 500).toISOString(), conversation_id: currentConvo.id };
      setMessages(function(p) { return p.concat([tmpL]); });
      var rL = await supabase.from('messages').insert([{ text: listenExtra, sender: 'zhizhi', conversation_id: currentConvo.id }]).select();
      if (rL.data && rL.data[0]) {
        setMessages(function(p) { return p.map(function(m) { return m.id === tmpL.id ? rL.data[0] : m; }); });
      }
    }

    var tmp = { id: 'tmp-' + Date.now(), text: t, sender: 'zhizhi', created_at: now, conversation_id: currentConvo.id, image_url: imageUrlVal, file_url: fileUrlVal, file_name: fileNameVal, video_url: videoUrlVal };
    setMessages(function(p) { return p.concat([tmp]); });
    setInput('');
    setImageFiles([]);
    setImagePreviews([]);
    setDocFiles([]);
    setDocNames([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (fileDocInputRef.current) fileDocInputRef.current.value = '';

    var insertData = { text: t, sender: 'zhizhi', conversation_id: currentConvo.id };
    if (imageUrlVal) insertData.image_url = imageUrlVal;
    if (fileUrlVal) insertData.file_url = fileUrlVal;
    if (fileNameVal) insertData.file_name = fileNameVal;
    if (videoUrlVal) insertData.video_url = videoUrlVal;
    if (videoFramesVal) insertData.video_frames = videoFramesVal;

    var r = await supabase.from('messages').insert([insertData]).select();
    if (r.error) {
      setMessages(function(p) { return p.filter(function(m) { return m.id !== tmp.id; }); });
      setInput(t);
      sendingRef.current = false;
      setSending(false);
      return;
    }
    if (r.data && r.data[0]) {
      setMessages(function(p) { return p.map(function(m) { return m.id === tmp.id ? r.data[0] : m; }); });
    }
    await supabase.from('conversations').update({ updated_at: now }).eq('id', currentConvo.id);

    try {
      var resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: currentConvo.id, message: (listenExtra ? listenExtra + '\n\n' + t : t), image_url: imageUrlVal, file_url: fileUrlVal, file_name: fileNameVal, video_frames: videoFramesVal, video_duration: videoDurationVal })
      });
      var data = await resp.json();
      if (data.reply) {
        var freshMsgs = await refreshRecent(currentConvo.id);   // [chat-delta]
        if (freshMsgs) {
          if (streamEnabled) {
            // 找最新的aiden消息，逐字显示
            var lastAidenMsg = null;
            for (var si = freshMsgs.length - 1; si >= 0; si--) {
              if (freshMsgs[si].sender === 'aiden' && freshMsgs[si].text) { lastAidenMsg = freshMsgs[si]; break; }
            }
            if (lastAidenMsg) {
              var fullText = lastAidenMsg.text;
              var targetId = lastAidenMsg.id;
              var displayMsgs = freshMsgs.map(function(m) { return m.id === targetId ? Object.assign({}, m, { text: '' }) : m; });
              setMessages(displayMsgs);
              var charIdx = 0;
              var chunkSize = 2;
              if (streamTimerRef.current) clearInterval(streamTimerRef.current);
              streamTimerRef.current = setInterval(function() {
                charIdx += chunkSize;
                if (charIdx >= fullText.length) {
                  clearInterval(streamTimerRef.current);
                  streamTimerRef.current = null;
                  setMessages(function(p) { return p.map(function(m) { return m.id === targetId ? Object.assign({}, m, { text: fullText }) : m; }); });
                } else {
                  setMessages(function(p) { return p.map(function(m) { return m.id === targetId ? Object.assign({}, m, { text: fullText.substring(0, charIdx) }) : m; }); });
                }
              }, 25);
            } else {
              setMessages(freshMsgs);
            }
          } else {
            setMessages(freshMsgs);
          }
        }
        if(data.bt_commands&&data.bt_commands.length>0){data.bt_commands.forEach(function(cmd){if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.nativeAction){window.webkit.messageHandlers.nativeAction.postMessage(cmd);}});}
        if(data.music_commands&&data.music_commands.length>0){setPendingMusic(data.music_commands[0]);}
        if(data.night_pass){data.night_pass.forEach(function(a){if(a==='show_form')setShowNightPassForm(true);if(a==='approved'){setNightPassApproved(true);setTimeout(function(){setNightPassApproved(false);},3000);}if(a==='rejected'){setNightPassRejected(true);setTimeout(function(){setNightPassRejected(false);},3000);}});}

      } else if (data.error) {
        console.error('API error:', data.error);
        var errMsg = { id: 'err-' + Date.now(), text: '[\u56DE\u590D\u5931\u8D25: ' + data.error + ']', sender: 'aiden', created_at: new Date().toISOString(), conversation_id: currentConvo.id };
        setMessages(function(p) { return p.concat([errMsg]); });
      }
    } catch (err) {
      console.error('Backend error:', err);
      var errMsg2 = { id: 'err-' + Date.now(), text: '[\u8FDE\u63A5\u540E\u7AEF\u5931\u8D25]', sender: 'aiden', created_at: new Date().toISOString(), conversation_id: currentConvo.id };
      setMessages(function(p) { return p.concat([errMsg2]); });
    }
    sendingRef.current = false;
    setSending(false);
  };

  var _hkParts = (function() {
    var f = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    return function(x) { var o = {}; f.formatToParts(x).forEach(function(p) { o[p.type] = p.value; }); return o; };
  })();
  var fmtTime = function(ts) {
    // 一切按香港墙钟显示，不跟手机时区走
    var d = _hkParts(new Date(ts));
    var now = _hkParts(new Date());
    var t = d.hour + ':' + d.minute;
    if (d.year === now.year && d.month === now.month && d.day === now.day) return t;
    if (d.year === now.year) return d.month + '/' + d.day + ' ' + t;
    return d.year + '/' + d.month + '/' + d.day + ' ' + t;
  };

  var showDateDiv = function(msg, i) {
    if (i === 0) return true;
    var p = _hkParts(new Date(messages[i - 1].created_at));
    var c = _hkParts(new Date(msg.created_at));
    return p.day !== c.day || p.month !== c.month || p.year !== c.year;
  };

  var fmtDateDiv = function(ts) {
    var d = _hkParts(new Date(ts));
    var now = _hkParts(new Date());
    if (d.year === now.year && d.month === now.month && d.day === now.day) return '\u4ECA\u5929';
    var yd = _hkParts(new Date(Date.now() - 86400000));
    if (d.year === yd.year && d.month === yd.month && d.day === yd.day) return '\u6628\u5929';
    if (d.year === now.year) return d.month + '\u6708' + d.day + '\u65E5';
    return d.year + '\u5E74' + d.month + '\u6708' + d.day + '\u65E5';
  };

  var deleteMessages = ['\u786E\u5B9A\u8981\u5220\u9664\u8FD9\u4E2A\u7A97\u53E3\u5417\uFF1F', '\u771F\u7684\u8981\u5220\u5417\uFF1F\u5220\u4E86\u5C31\u6CA1\u4E86\u3002', '\u6700\u540E\u4E00\u6B21\u786E\u8BA4\u3002\u4E0D\u53EF\u6062\u590D\u3002'];

  // 解析image_url/file_url：兼容单个URL字符串和JSON数组
  var parseUrls = function(val) {
    if (!val) return [];
    if (val.startsWith('[')) {
      try { return JSON.parse(val); } catch(e) { return [val]; }
    }
    return [val];
  };

  var parseNames = function(val) {
    if (!val) return [];
    if (val.startsWith('[')) {
      try { return JSON.parse(val); } catch(e) { return [val]; }
    }
    return [val];
  };

  var parseAttachments = function(val) {
    if (!val) return [];
    try { return JSON.parse(val); } catch(e) { return []; }
  };

  // 媒体块：视频+图片无气泡直出，多图 iMessage 式拼贴（1直出/2并排/3一大两小/4+网格带+N）
  var renderMediaBlock = function(msg) {
    var imgs = parseUrls(msg.image_url);
    var hasVideo = !!msg.video_url;
    if (!hasVideo && imgs.length === 0) return null;
    var shown = imgs.slice(0, 4);
    var extra = imgs.length - 4;
    var cls = imgs.length === 1 ? 'n1' : imgs.length === 2 ? 'n2' : imgs.length === 3 ? 'n3' : 'n4plus';
    return (
      <div className="msg-media-block">
        {hasVideo && <video src={msg.video_url} controls playsInline preload="metadata" />}
        {imgs.length > 0 && (
          <div className={'msg-media-collage ' + cls}>
            {shown.map(function(url, idx) {
              return (
                <div className="collage-item" key={idx} onClick={function() { setFullscreenImg(url); }}>
                  <img src={url} alt="" />
                  {idx === 3 && extra > 0 && <div className="collage-more">{'+' + extra}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  var getAttachmentIcon = function(type) {
    if (type === 'html') return '\u{1F3AA}';
    if (type === 'js') return '\u26A1';
    if (type === 'css') return '\u{1F3A8}';
    if (type === 'json') return '\u{1F4CB}';
    if (type === 'md') return '\u{1F4DD}';
    if (type === 'pdf') return '\u{1F4D5}';
    if (type === 'txt') return '\u{1F4C4}';
    return '\u{1F4CE}';
  };

  var renderMessage = function(msg, i, groupKey, groupMsgs, groups, hideTime) {
    // 图片消息的'[图片]'占位文字不显示（占位只给AI看的）
    if (msg.image_url && msg.text === '[\u56FE\u7247]') msg = Object.assign({}, msg, { text: '' });
    var isAiden = msg.sender === 'aiden';
    var isEditing = editingId === msg.id;
    var total = groupMsgs ? groupMsgs.length : 1;
    var currentIdx = groupKey ? getGenIndex(groupKey, total) : 0;
    var showNav = total > 1;
    var isLast = groupKey ? isLastAidenGroup(groupKey, groups) : false;

    // 表情包消息：不套气泡，直接显示大图，带头像
    var isStickerMsg = (msg.text === '[sticker]' || (msg.text && msg.text.startsWith('[表情包'))) && msg.image_url;
    if (isStickerMsg) {
      var stickerUrls = parseUrls(msg.image_url);
      var isAidenSticker = msg.sender === 'aiden';
      return (
        <React.Fragment key={msg.id}>
          <div data-mid={msg.id} className={'msg-row ' + msg.sender}>
            {showAvatar && bubbleMode && isAidenSticker && <div className="msg-avatar aiden">{avatarHim ? <img src={avatarHim} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameHim}</div>}
            <div className="msg-content-wrap">
              <div className="sticker-wrap">
                {stickerUrls.map(function(url, idx) {
                  return <img key={idx} src={url} alt="" className="sticker-img" onClick={function() { setFullscreenImg(url); }} />;
                })}
              </div>
            </div>
            {showAvatar && bubbleMode && !isAidenSticker && <div className="msg-avatar zhizhi">{avatarMe ? <img src={avatarMe} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameMe}</div>}
          </div>
        </React.Fragment>
      );
    }

    // 无气泡模式 - aiden语音消息
    if (!bubbleMode && isAiden && msg.audio_url) {
      return (
        <React.Fragment key={msg.id}>
          <div data-mid={msg.id} className={'msg-flat ' + msg.sender}>
            <div className="msg-flat-header">
              <span className="msg-flat-name">{nicknameHim}</span>
              {!hideTime && <span className="msg-flat-time">{fmtTime(msg.created_at)}</span>}
            </div>
            <VoiceBar msg={msg} />
          </div>
        </React.Fragment>
      );
    }
    // 无气泡模式（aiden消息）
    if (!bubbleMode && isAiden) {
      return (
        <React.Fragment key={msg.id}>
          <div data-mid={msg.id} className={'msg-flat ' + msg.sender}>
            <div className="msg-flat-header">
              <span className="msg-flat-name">{nicknameHim}</span>
              {!hideTime && <span className="msg-flat-time">{fmtTime(msg.created_at)}</span>}
            </div>
            {msg.thinking && (
              <div className="msg-thinking-wrap">
                <button className="msg-thinking-toggle" onClick={function() { toggleThinking(msg.id); }}>
                  {expandedThinking[msg.id] ? '\u25BE \u601D\u8003\u8FC7\u7A0B' : '\u25B8 \u601D\u8003\u8FC7\u7A0B'}
                </button>
                {expandedThinking[msg.id] && <div className="msg-thinking-content">{msg.thinking}</div>}
              </div>
            )}
            {msg.tool_calls && (
              <div className="msg-thinking-wrap">
                <button className="msg-thinking-toggle" onClick={function() { toggleThinking('tool-' + msg.id); }}>
                  {expandedThinking['tool-' + msg.id] ? '\u25BE \u5DE5\u5177\u8C03\u7528' : '\u25B8 \u5DE5\u5177\u8C03\u7528'}
                </button>
                {expandedThinking['tool-' + msg.id] && (
                  <div className="msg-thinking-content">
                    {(formatToolCalls(msg.tool_calls) || []).map(function(tc, i) {
                      return <div key={i}>{tc.icon + ' ' + tc.name + (tc.detail ? ': ' + tc.detail : '')}</div>;
                    })}
                  </div>
                )}
              </div>
            )}
            {renderMediaBlock(msg)}
            {parseUrls(msg.file_url).map(function(url, idx) {
              var names = parseNames(msg.file_name);
              var name = names[idx] || '\u6587\u4EF6';
              return (
                <div className="msg-file-wrap" key={'file-'+idx} onClick={function() { window.open(url, '_blank'); }}>
                  <span className="msg-file-icon">{getFileIcon(name)}</span>
                  <span className="msg-file-name">{name}</span>
                </div>
              );
            })}
            {parseAttachments(msg.attachments).map(function(att, idx) {
              var isHtml = att.type === 'html';
              return (
                <div className={'msg-attachment-card' + (isHtml ? ' html' : '')} key={'att-'+idx} onClick={function() { if (isHtml) { openHtmlPopup(att.url); } }}>
                  <span className="attachment-icon">{getAttachmentIcon(att.type)}</span>
                  <div className="attachment-info">
                    <span className="attachment-name">{att.filename}</span>
                    <span className="attachment-desc">{att.description || att.filename}</span>
                  </div>
                  <div className="attachment-actions">
                    <button className="attachment-action-btn" onClick={function(e) { downloadAttachment(e, att.url, att.filename); }} title="下载">&#8675;</button>
                    <button className="attachment-action-btn" onClick={function(e) { shareAttachment(e, att.url, att.filename); }} title="分享">&#8599;</button>
                    {isHtml && <button className="attachment-action-btn play" onClick={function(e) { e.stopPropagation(); openHtmlPopup(att.url); }} title="打开">&#9654;</button>}
                  </div>
                </div>
              );
            })}
            {msg.text && <div className="msg-flat-text">{renderMessageText(msg.text, msg.id)}</div>}
            <div className="msg-toolbar">
              {showNav && (
                <div className="toolbar-nav">
                  <button className="toolbar-btn" disabled={currentIdx <= 0} onClick={function() { setGenIndex(groupKey, currentIdx - 1); }}>{'\u2039'}</button>
                  <span className="toolbar-counter">{(currentIdx + 1) + ' / ' + total}</span>
                  <button className="toolbar-btn" disabled={currentIdx >= total - 1} onClick={function() { setGenIndex(groupKey, currentIdx + 1); }}>{'\u203A'}</button>
                </div>
              )}
              <div className="toolbar-actions">
                <button className={'toolbar-btn' + (copiedId === msg.id ? ' copied' : '')} onClick={function() { copyText(msg.id, msg.text); }}>{copiedId === msg.id ? '\u2713' : '\u2398'}</button>
                <button className={'toolbar-btn' + (kbSaved === msg.id ? ' copied' : '')} onClick={function() { saveToKnowledge(msg.id, msg.text); }}>{kbSaved === msg.id ? '\u2713' : '\u229B'}</button>
                {ttsEnabled && msg.text && !msg.audio_url && <button className={'toolbar-btn tts-btn' + (ttsPlayingId === msg.id ? ' tts-playing' : '') + (ttsLoadingId === msg.id ? ' tts-loading' : '')} onTouchEnd={function(e) { e.preventDefault(); playTts(msg.id, msg.text); }} onClick={function() { playTts(msg.id, msg.text); }}>{ttsLoadingId === msg.id ? React.createElement('svg', { className: 'tts-spinner', width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2' }, React.createElement('circle', { cx: 12, cy: 12, r: 10 })) : React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('polygon', { points: '11 5 6 9 2 9 2 15 6 15 11 19 11 5' }), React.createElement('path', { d: ttsPlayingId === msg.id ? 'M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14' : 'M15.54 8.46a5 5 0 0 1 0 7.07' }))}</button>}
                {isLast && <button className="toolbar-btn" onClick={function() { retryMessage(msg.id); }} disabled={sending}>{'\u21BB'}</button>}
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }

    // 气泡模式 - aiden
    // aiden语音消息渲染为语音条
    if (isAiden && msg.audio_url) {
      return (
        <React.Fragment key={msg.id}>
          <div data-mid={msg.id} className="msg-row aiden">
            {showAvatar && <div className="msg-avatar aiden">{avatarHim ? <img src={avatarHim} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameHim}</div>}
            <div className="msg-content-wrap">
              <VoiceBar msg={msg} />
              <div className="msg-toolbar">
                {!hideTime && <span className="msg-time-outer">{fmtTime(msg.created_at)}</span>}
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }
    if (isAiden) {
      return (
        <React.Fragment key={msg.id}>
          <div data-mid={msg.id} className="msg-row aiden">
            {showAvatar && <div className="msg-avatar aiden">{avatarHim ? <img src={avatarHim} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameHim}</div>}
            <div className="msg-content-wrap">
              {msg.thinking && (
                <div className="msg-thinking-wrap">
                  <button className="msg-thinking-toggle" onClick={function() { toggleThinking(msg.id); }}>
                    {expandedThinking[msg.id] ? '\u25BE \u601D\u8003\u8FC7\u7A0B' : '\u25B8 \u601D\u8003\u8FC7\u7A0B'}
                  </button>
                  {expandedThinking[msg.id] && <div className="msg-thinking-content">{msg.thinking}</div>}
                </div>
              )}
              {msg.tool_calls && (
                <div className="msg-thinking-wrap">
                  <button className="msg-thinking-toggle" onClick={function() { toggleThinking('tool-' + msg.id); }}>
                    {expandedThinking['tool-' + msg.id] ? '\u25BE \u5DE5\u5177\u8C03\u7528' : '\u25B8 \u5DE5\u5177\u8C03\u7528'}
                  </button>
                  {expandedThinking['tool-' + msg.id] && (
                    <div className="msg-thinking-content">
                      {(formatToolCalls(msg.tool_calls) || []).map(function(tc, i) {
                        return <div key={i}>{tc.icon + ' ' + tc.name + (tc.detail ? ': ' + tc.detail : '')}</div>;
                      })}
                    </div>
                  )}
                </div>
              )}
              {renderMediaBlock(msg)}
              {(msg.text || parseUrls(msg.file_url).length > 0 || parseAttachments(msg.attachments).length > 0 || msg.starred) && <div className="msg-bubble aiden">
                {parseUrls(msg.file_url).map(function(url, idx) {
                  var names = parseNames(msg.file_name);
                  var name = names[idx] || '\u6587\u4EF6';
                  return (
                    <div className="msg-file-wrap" key={'file-'+idx} onClick={function() { window.open(url, '_blank'); }}>
                      <span className="msg-file-icon">{getFileIcon(name)}</span>
                      <span className="msg-file-name">{name}</span>
                    </div>
                  );
                })}
                {parseAttachments(msg.attachments).map(function(att, idx) {
                  var isHtml = att.type === 'html';
                  return (
                    <div className={'msg-attachment-card' + (isHtml ? ' html' : '')} key={'att-'+idx} onClick={function() { if (isHtml) { openHtmlPopup(att.url); } }}>
                      <span className="attachment-icon">{getAttachmentIcon(att.type)}</span>
                      <div className="attachment-info">
                        <span className="attachment-name">{att.filename}</span>
                        <span className="attachment-desc">{att.description || att.filename}</span>
                      </div>
                      <div className="attachment-actions">
                        <button className="attachment-action-btn" onClick={function(e) { downloadAttachment(e, att.url, att.filename); }} title="下载">&#8675;</button>
                        <button className="attachment-action-btn" onClick={function(e) { shareAttachment(e, att.url, att.filename); }} title="分享">&#8599;</button>
                        {isHtml && <button className="attachment-action-btn play" onClick={function(e) { e.stopPropagation(); openHtmlPopup(att.url); }} title="打开">&#9654;</button>}
                      </div>
                    </div>
                  );
                })}
                {msg.text && <div className="msg-text">{renderMessageText(msg.text, msg.id)}</div>}
                {msg.starred && <div className="msg-star-badge">{'\u2B50'}{msg.star_label ? ' ' + msg.star_label : ''}</div>}
              </div>}
              <div className="msg-toolbar">
                {!hideTime && <span className="msg-time-outer">{fmtTime(msg.created_at)}</span>}
                {showNav && (
                  <div className="toolbar-nav">
                    <button className="toolbar-btn" disabled={currentIdx <= 0} onClick={function() { setGenIndex(groupKey, currentIdx - 1); }}>{'\u2039'}</button>
                    <span className="toolbar-counter">{(currentIdx + 1) + ' / ' + total}</span>
                    <button className="toolbar-btn" disabled={currentIdx >= total - 1} onClick={function() { setGenIndex(groupKey, currentIdx + 1); }}>{'\u203A'}</button>
                  </div>
                )}
                <div className="toolbar-actions">
                  <button className={'toolbar-btn' + (msg.starred ? ' starred' : '')} onClick={function() { toggleStar(msg); }} title={msg.starred ? '\u53D6\u6D88\u661F\u6807' : '\u52A0\u661F\u6807'}>{msg.starred ? '\u2605' : '\u2606'}</button>
                  <button className={'toolbar-btn' + (copiedId === msg.id ? ' copied' : '')} onClick={function() { copyText(msg.id, msg.text); }}>{copiedId === msg.id ? '\u2713' : '\u2398'}</button>
                  <button className={'toolbar-btn' + (kbSaved === msg.id ? ' copied' : '')} onClick={function() { saveToKnowledge(msg.id, msg.text); }}>{kbSaved === msg.id ? '\u2713' : '\u229B'}</button>
                  {ttsEnabled && msg.text && !msg.audio_url && <button className={'toolbar-btn tts-btn' + (ttsPlayingId === msg.id ? ' tts-playing' : '') + (ttsLoadingId === msg.id ? ' tts-loading' : '')} onTouchEnd={function(e) { e.preventDefault(); playTts(msg.id, msg.text); }} onClick={function() { playTts(msg.id, msg.text); }}>{ttsLoadingId === msg.id ? React.createElement('svg', { className: 'tts-spinner', width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2' }, React.createElement('circle', { cx: 12, cy: 12, r: 10 })) : React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('polygon', { points: '11 5 6 9 2 9 2 15 6 15 11 19 11 5' }), React.createElement('path', { d: ttsPlayingId === msg.id ? 'M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14' : 'M15.54 8.46a5 5 0 0 1 0 7.07' }))}</button>}
                  {isLast && <button className="toolbar-btn" onClick={function() { retryMessage(msg.id); }} disabled={sending}>{'\u21BB'}</button>}
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }

    // zhizhi消息
    // 语音消息渲染为语音条
    if (msg.audio_url) {
      return (
        <React.Fragment key={msg.id}>
          <div data-mid={msg.id} className="msg-row zhizhi">
            <div className="msg-content-wrap">
              <VoiceBar msg={msg} />
              <div className="msg-toolbar msg-toolbar-right">
                {!hideTime && <span className="msg-time-outer">{fmtTime(msg.created_at)}</span>}
              </div>
            </div>
            {showAvatar && <div className="msg-avatar zhizhi">{avatarMe ? <img src={avatarMe} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameMe}</div>}
          </div>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={msg.id}>
        <div data-mid={msg.id} className="msg-row zhizhi">
          <div className="msg-content-wrap">
            {renderMediaBlock(msg)}
            {(isEditing || msg.text || parseUrls(msg.file_url).length > 0 || msg.starred || (editBranches[String(msg.id)] && editBranches[String(msg.id)].length > 0)) && <div className="msg-bubble zhizhi">
              {parseUrls(msg.file_url).map(function(url, idx) {
                var names = parseNames(msg.file_name);
                var name = names[idx] || '\u6587\u4EF6';
                return (
                  <div className="msg-file-wrap" key={'file-'+idx} onClick={function() { window.open(url, '_blank'); }}>
                    <span className="msg-file-icon">{getFileIcon(name)}</span>
                    <span className="msg-file-name">{name}</span>
                  </div>
                );
              })}
              {isEditing ? (
                <div className="msg-edit-wrap">
                  <textarea className="msg-edit-input" value={editText} onChange={function(e) { setEditText(e.target.value); }} autoFocus rows={3} />
                  <div className="msg-edit-actions">
                    <button className="msg-edit-btn save" onClick={function() { saveEdit(msg.id); }}>{'\u4FDD\u5B58'}</button>
                    <button className="msg-edit-btn regen" onClick={function() { saveEditAndRegenerate(msg.id); }} disabled={sending}>{sending ? '...' : '\u4FDD\u5B58\u5E76\u91CD\u65B0\u751F\u6210'}</button>
                    <button className="msg-edit-btn cancel" onClick={cancelEdit}>{'\u53D6\u6D88'}</button>
                  </div>
                </div>
              ) : (
                msg.text && <div className="msg-text">{renderMessageText(msg.text, msg.id)}</div>
              )}
              {msg.starred && <div className="msg-star-badge">{'\u2B50'}{msg.star_label ? ' ' + msg.star_label : ''}</div>}
              {editBranches[String(msg.id)] && editBranches[String(msg.id)].length > 0 && !isEditing && (
                <div style={{display:'flex', alignItems:'center', gap:'6px', marginTop:'4px', paddingTop:'4px', borderTop:'1px solid rgba(0,0,0,0.06)'}}>
                  <span style={{fontSize:'11px', opacity:0.5}}>{'\u270E \u5DF2\u7F16\u8F91'}</span>
                  <button style={{background:'none', border:'1px solid rgba(0,0,0,0.15)', borderRadius:'4px', color:'inherit', cursor:'pointer', fontSize:'11px', padding:'2px 8px', opacity:0.6}}
                    onClick={function() {
                      var branches = editBranches[String(msg.id)];
                      if (branches && branches.length > 0) switchEditBranch(msg.id, branches[branches.length - 1]);
                    }}
                    disabled={sending}>
                    {'\u21A9 \u65E7\u7248\u672C (' + editBranches[String(msg.id)].length + ')'}
                  </button>
                </div>
              )}
            </div>}
            {!isEditing && (
              <div className="msg-toolbar msg-toolbar-right">
                <div className="toolbar-actions">
                  <button className={'toolbar-btn' + (msg.starred ? ' starred' : '')} onClick={function() { toggleStar(msg); }}>{msg.starred ? '\u2605' : '\u2606'}</button>
                  <button className="toolbar-btn" onClick={function() { startEdit(msg); }}>{'\u270E'}</button>
                  <button className={'toolbar-btn' + (copiedId === msg.id ? ' copied' : '')} onClick={function() { copyText(msg.id, msg.text); }}>{copiedId === msg.id ? '\u2713' : '\u2398'}</button>
                </div>
                {!hideTime && <span className="msg-time-outer">{fmtTime(msg.created_at)}</span>}
              </div>
            )}
          </div>
          {showAvatar && <div className="msg-avatar zhizhi">{avatarMe ? <img src={avatarMe} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameMe}</div>}
        </div>
      </React.Fragment>
    );
  };

  // 构建分组并渲染
  var displayGroups = buildDisplayGroups();

  var renderAllMessages = function() {
    var result = [];
    var lastDate = null;
    // 同发送者同一分钟连发的消息：只有最后一条显示时间戳
    var sameMinuteAsNext = function(g2, curMsg) {
      var nextGroup = displayGroups[g2 + 1];
      if (!nextGroup) return false;
      var curGroup = displayGroups[g2];
      if (nextGroup.sender !== curGroup.sender) return false;
      return fmtTime(nextGroup.msgs[0].created_at) === fmtTime(curMsg.created_at);
    };
    // 纯图片消息判断（分条模式一张图一条消息，text是'[图片]'占位）
    var isPureImageMsg = function(m) {
      if (!m || m.sender !== 'zhizhi' || !m.image_url) return false;
      if (m.file_url || m.video_url || m.audio_url) return false;
      if (m.text && m.text !== '[\u56FE\u7247]') return false;
      return true;
    };
    for (var g = 0; g < displayGroups.length; g++) {
      var group = displayGroups[g];
      if (group.sender === 'zhizhi') {
        var msg = group.msgs[0];
        // 分条发的多张图：相邻+同一分钟的纯图片消息合并成一个iMessage拼贴
        if (isPureImageMsg(msg)) {
          var runUrls = parseUrls(msg.image_url);
          var gEnd = g + 1;
          while (gEnd < displayGroups.length && displayGroups[gEnd].sender === 'zhizhi'
            && isPureImageMsg(displayGroups[gEnd].msgs[0])
            && fmtTime(displayGroups[gEnd].msgs[0].created_at) === fmtTime(msg.created_at)) {
            runUrls = runUrls.concat(parseUrls(displayGroups[gEnd].msgs[0].image_url));
            gEnd++;
          }
          if (gEnd > g + 1) {
            var lastImgMsg = displayGroups[gEnd - 1].msgs[0];
            var collageMsg = Object.assign({}, lastImgMsg, { image_url: JSON.stringify(runUrls), text: '' });
            var oiC = messages.indexOf(msg);
            if (showDateDiv(msg, oiC)) {
              result.push(<div className="date-divider" key={'d-' + msg.id}><span>{fmtDateDiv(msg.created_at)}</span></div>);
            }
            result.push(renderMessage(collageMsg, oiC, null, null, null, sameMinuteAsNext(gEnd - 1, lastImgMsg)));
            g = gEnd - 1;
            continue;
          }
        }
        var origIdx = messages.indexOf(msg);
        if (showDateDiv(msg, origIdx)) {
          result.push(<div className="date-divider" key={'d-' + msg.id}><span>{fmtDateDiv(msg.created_at)}</span></div>);
        }
        result.push(renderMessage(msg, origIdx, null, null, null, sameMinuteAsNext(g, msg)));
      } else {
        // aiden组
        var total = group.msgs.length;
        var origIdx2 = messages.indexOf(group.msgs[0]);
        if (showDateDiv(group.msgs[0], origIdx2)) {
          result.push(<div className="date-divider" key={'d-' + group.msgs[0].id}><span>{fmtDateDiv(group.msgs[0].created_at)}</span></div>);
        }
        // 无气泡模式且全是active消息：合并文字成一段，不显示切换器
        var allActive = group.msgs.every(function(m) { return m.is_active !== false; });
        if (!bubbleMode && total > 1 && allActive) {
          var mergedText = group.msgs.map(function(m) { return m.text; }).join('\n\n');
          var mergedMsg = Object.assign({}, group.msgs[0], { text: mergedText });
          result.push(renderMessage(mergedMsg, origIdx2, null, null, null, sameMinuteAsNext(g, mergedMsg)));
        } else {
          var selectedIdx = getGenIndex(group.key, total);
          var selectedMsg = group.msgs[selectedIdx];
          result.push(renderMessage(selectedMsg, origIdx2, group.key, group.msgs, displayGroups, sameMinuteAsNext(g, selectedMsg)));
        }
      }
    }
    return result;
  };

  return (
    <div className={'chat-page' + (currentConvo && currentConvo.room === 'silas' ? ' silas-room' : '') + (localStorage.getItem('hide_msg_actions') === 'true' ? ' hide-actions' : '') + (chatBgUrl ? ' glass-on' : '')}>
      <style>{`
        .msg-attachment-card { display: flex; align-items: center; gap: 10px; padding: 10px 14px; margin: 6px 0; border-radius: 10px; background: var(--bg-secondary, #f5f0ec); cursor: pointer; transition: background 0.2s; border: 1px solid var(--border-color, #e8e0d8); }
        .msg-attachment-card:hover { background: var(--bg-hover, #ece5dd); }
        .msg-attachment-card.html { background: linear-gradient(135deg, var(--bg-secondary, #f5f0ec), var(--accent-light, #fef3e8)); border-color: var(--accent-color, #d4a574); }
        .attachment-icon { font-size: 24px; flex-shrink: 0; }
        .attachment-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .attachment-name { font-size: 13px; font-weight: 600; color: var(--text-primary, #2c2c2c); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .attachment-desc { font-size: 11px; color: var(--text-secondary, #8a8a8a); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .attachment-actions { display: flex; gap: 4px; flex-shrink: 0; }
        .attachment-action-btn { width: 30px; height: 30px; border-radius: 50%; border: none; background: transparent; color: var(--text-secondary, #8a8a8a); font-size: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .attachment-action-btn:hover { background: var(--bg-hover, #ece5dd); color: var(--text-primary, #2c2c2c); }
        .attachment-action-btn.play { color: var(--accent-color, #d4a574); }
        .html-popup-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease; }
        .html-popup-container { position: relative; width: 92vw; height: 88vh; max-width: 800px; border-radius: 12px; overflow: hidden; background: #fff; box-shadow: 0 20px 60px rgba(0,0,0,0.5); }
        .html-popup-close { position: absolute; top: 8px; right: 8px; z-index: 10000; width: 32px; height: 32px; border-radius: 50%; border: none; background: rgba(0,0,0,0.5); color: #fff; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s; }
        .html-popup-close:hover { background: rgba(0,0,0,0.7); }
        .html-popup-iframe { width: 100%; height: 100%; border: none; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .msg-row.sticker { display: flex; justify-content: flex-end; padding: 4px 16px; }
        .msg-row.sticker.aiden { justify-content: flex-start; }
        .sticker-wrap { max-width: 140px; }
        .sticker-img { width: 100%; height: auto; max-height: 140px; object-fit: contain; cursor: pointer; border-radius: 4px; }
        .sticker-panel-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.3); z-index: 998; }
        .sticker-panel { position: fixed; bottom: 0; left: 0; right: 0; z-index: 999; background: var(--bg-primary, #faf6f1); border-top-left-radius: 18px; border-top-right-radius: 18px; box-shadow: 0 -4px 24px rgba(0,0,0,0.12); height: 50vh; display: flex; flex-direction: column; animation: slideUp 0.28s cubic-bezier(0.32,0.72,0,1); overflow: hidden; }
        .sticker-panel-drag-bar { display: flex; justify-content: center; padding: 8px 0 2px; flex-shrink: 0; }
        .sticker-panel-drag-bar::after { content: ''; width: 36px; height: 4px; border-radius: 2px; background: var(--border-color, #d4ccc4); }
        .sticker-panel-header { display: flex; align-items: center; justify-content: space-between; padding: 4px 16px 10px; flex-shrink: 0; }
        .sticker-panel-title { font-size: 15px; font-weight: 600; color: var(--text-primary, #2c2c2c); }
        .sticker-panel-close { background: none; border: none; font-size: 20px; color: var(--text-secondary, #8a8a8a); cursor: pointer; padding: 4px 6px; line-height: 1; }
        .sticker-grid-wrapper { flex: 1; overflow-y: scroll; -webkit-overflow-scrolling: touch; overscroll-behavior-y: contain; min-height: 0; }
        .sticker-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; padding: 6px 14px; padding-bottom: max(20px, env(safe-area-inset-bottom)); }
        .sticker-add { aspect-ratio: 1; border: 2px dashed var(--border-color, #d4ccc4); border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.2s; }
        .sticker-add span { font-size: 28px; color: var(--text-secondary, #8a8a8a); }
        .sticker-add:active { border-color: var(--accent-color, #d4a574); }
        .sticker-item { aspect-ratio: 1; border-radius: 12px; overflow: hidden; cursor: pointer; display: flex; align-items: center; justify-content: center; background: var(--bg-secondary, #f0ebe5); transition: transform 0.15s; position: relative; }
        .sticker-item:active { transform: scale(0.92); }
        .sticker-item img { width: 85%; height: 85%; object-fit: contain; }
        .sticker-delete-btn { position: absolute; top: 3px; right: 3px; width: 18px; height: 18px; border-radius: 50%; background: rgba(0,0,0,0.45); color: white; border: none; font-size: 11px; display: flex; align-items: center; justify-content: center; cursor: pointer; line-height: 1; padding: 0; backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .music-card { display: flex; align-items: center; gap: 10px; padding: 8px 14px; margin: 6px 16px; border-radius: 12px; background: var(--card, #FFFDF9); border: 1px solid rgba(0,0,0,0.06); box-shadow: 0 1px 4px rgba(0,0,0,0.04); position: relative; overflow: hidden; cursor: pointer; }
        .music-card-progress { position: absolute; top: 0; left: 0; height: 2px; background: var(--accent, #d4a574); transition: width 0.3s linear; z-index: 1; }
        .music-card-cover { width: 32px; height: 32px; border-radius: 7px; object-fit: cover; flex-shrink: 0; z-index: 1; }
        .music-card-icon { width: 32px; height: 32px; border-radius: 7px; background: var(--accent, #d4a574); color: white; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; z-index: 1; }
        .music-card-info { flex: 1; min-width: 0; z-index: 1; }
        .music-card-name { font-size: 13.5px; font-weight: 600; color: var(--text, #2c2c2c); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .music-card-singer { font-size: 11px; color: var(--text4, #999); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .music-card-play { width: 30px; height: 30px; border-radius: 50%; border: none; background: var(--accent, #d4a574); color: white; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; cursor: pointer; z-index: 2; -webkit-tap-highlight-color: transparent; }
        .music-card-play:active { opacity: 0.8; }
        .music-card-close { background: none; border: none; color: var(--text4, #999); font-size: 18px; cursor: pointer; padding: 4px; z-index: 2; flex-shrink: 0; -webkit-tap-highlight-color: transparent; }

        .search-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 200; display: flex; flex-direction: column; }
        .search-panel { flex: 1; display: flex; flex-direction: column; background: var(--bg-primary, #faf7f4); max-width: 600px; margin: 0 auto; width: 100%; }
        .search-header { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-bottom: 1px solid var(--border-color, #e8e0d8); background: var(--bg-primary, #faf7f4); flex-shrink: 0; }
        .search-back { background: none; border: none; font-size: 20px; cursor: pointer; color: var(--text-primary, #333); padding: 4px 8px; }
        .search-input { flex: 1; border: 1px solid var(--border-color, #e8e0d8); border-radius: 8px; padding: 8px 12px; font-size: 14px; outline: none; background: var(--bg-secondary, #f0ebe5); color: var(--text-primary, #333); font-family: inherit; }
        .search-input:focus { border-color: var(--accent-color, #d4a574); }
        .search-go { background: var(--accent-color, #d4a574); color: white; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; }
        .search-go:disabled { opacity: 0.5; }
        .search-results { flex: 1; overflow-y: auto; padding: 8px 12px; }
        .search-loading, .search-empty { text-align: center; color: var(--text-secondary, #888); padding: 40px 0; font-size: 13px; }
        .search-result-item { padding: 12px; margin-bottom: 8px; border-radius: 10px; background: var(--bg-secondary, #f0ebe5); cursor: pointer; transition: background 0.15s; border: 1px solid var(--border-color, #e8e0d8); }
        .search-result-item:hover { background: var(--bg-hover, #ece5dd); }
        .search-result-item:active { transform: scale(0.98); }
        .search-result-title { font-size: 14px; font-weight: 600; color: var(--accent-color, #d4a574); margin-bottom: 4px; line-height: 1.4; }
        .search-result-url { font-size: 11px; color: var(--text-secondary, #888); margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .search-result-snippet { font-size: 12px; color: var(--text-primary, #333); line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

        .browser-overlay { position: fixed; inset: 0; z-index: 250; background: var(--bg-primary, #faf7f4); display: flex; flex-direction: column; }
        .browser-header { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid var(--border-color, #e8e0d8); background: var(--bg-primary, #faf7f4); flex-shrink: 0; }
        .browser-close { background: none; border: none; font-size: 14px; cursor: pointer; color: var(--accent-color, #d4a574); padding: 6px 10px; font-family: inherit; white-space: nowrap; }
        .browser-url { flex: 1; font-size: 11px; color: var(--text-secondary, #888); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; background: var(--bg-secondary, #f0ebe5); padding: 6px 10px; border-radius: 6px; }
        .browser-iframe { flex: 1; border: none; width: 100%; }
      `}</style>
      {showSidebar && <div className="chat-sidebar-overlay" onClick={function() { setShowSidebar(false); }}></div>}
      <div className={'chat-sidebar' + (showSidebar ? ' open' : '')}>
        <div className="chat-sidebar-header">
          <div className="chat-sidebar-title">{'\u804A\u5929\u8BB0\u5F55'}</div>
        {(currentConvo && currentConvo.room === 'silas') ? (
          <button className="room-toggle-btn active" onClick={function() { exitSilas(); setShowSidebar(false); }} title={'\u56DE\u65E5\u5E38'}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        ) : (
          <button className="room-toggle-btn" onClick={enterSilas} title="Silas">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 4h3a2 2 0 0 1 2 2v14"/><path d="M2 20h3"/><path d="M13 20h9"/><path d="M10 12v.01"/><path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.562z"/></svg>
          </button>
        )}
          <button className="chat-recovery-btn" onClick={function() {
            setShowSidebar(false);
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeAction) {
              window.webkit.messageHandlers.nativeAction.postMessage('openRecovery');
            } else {
              window.open('/recovery', '_blank');
            }
          }} title="Recovery">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
          </button>
          <button className="sidebar-close" onClick={function() { setShowSidebar(false); }}>&times;</button>
        </div>
        <div className="chat-sidebar-list">
          {convos.filter(function(c) {
            if (c.room === 'lounge') return false;
            var inSilas = currentConvo && currentConvo.room === 'silas';
            return inSilas ? c.room === 'silas' : c.room !== 'silas';
          }).map(function(c) { return (
            <div key={c.id} className={'chat-sidebar-item' + (currentConvo && currentConvo.id === c.id ? ' active' : '')}>
              {renaming === c.id ? (
                <div className="rename-wrap">
                  <input className="rename-input" value={renameText} onChange={function(e) { setRenameText(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') doRename(); }} autoFocus />
                  <button className="rename-ok" onClick={doRename}>OK</button>
                </div>
              ) : (
                <div className="chat-sidebar-item-inner" onClick={function() { switchConvo(c); }}>
                  <div className="chat-sidebar-item-title">{c.title}</div>
                  <div className="chat-sidebar-item-actions">
                    <button className="sidebar-action-btn" onClick={function(e) { e.stopPropagation(); startRename(c); }}>&#9998;</button>
                    <button className="sidebar-action-btn del" onClick={function(e) { e.stopPropagation(); startDelete(c.id); }}>&#10005;</button>
                  </div>
                </div>
              )}
              {deleting === c.id && (
                <div className="delete-confirm">
                  <div className="delete-msg">{deleteMessages[deleteStep - 1]}</div>
                  <div className="delete-btns">
                    <button className="delete-yes" onClick={confirmDelete}>{'\u786E\u5B9A'}</button>
                    <button className="delete-no" onClick={cancelDelete}>{'\u53D6\u6D88'}</button>
                  </div>
                </div>
              )}
            </div>
          ); })}
        </div>
      </div>
      <div className="chat-header">
        <button className="chat-sidebar-btn" onClick={function() { setShowSidebar(true); }}>&#9776;</button>
        <div className="header-info" onClick={function() { setHeaderExpanded(!headerExpanded); }}>
          <div className="header-name-row">
            <div className={'header-name' + (headerExpanded ? '' : ' collapsed')}>{currentConvo ? currentConvo.title : '\u804A\u5929'}</div>
            <span className="header-expand-icon">{headerExpanded ? '\u25B4' : '\u25BE'}</span>
          </div>
          {headerExpanded && <div className="header-status">{modelName || '\u672A\u8BBE\u7F6E\u6A21\u578B'}</div>}
        </div>
        {toolsOpen && (
          <div style={{display:'flex',gap:2,alignItems:'center',animation:'toolsSlideIn 0.25s ease-out'}}>
            <button style={{background:'none',border:'none',padding:'4px 6px',cursor:'pointer',color:'var(--text-secondary, #9E8E7E)'}} onClick={function() { navigate('/pulse'); }} title="脉">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            </button>
            <button className="chat-bubble-toggle" onClick={toggleBubbleMode} title={bubbleMode ? '\u5207\u6362\u65E0\u6C14\u6CE1' : '\u5207\u6362\u6C14\u6CE1'} style={{display:'flex',alignItems:'center'}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={bubbleMode ? 'currentColor' : 'none'} fillOpacity={bubbleMode ? 0.22 : 1} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </button>
            <button className={'chat-extend-btn' + (isExtended ? ' active' : '')} onClick={toggleExtend} title={isExtended ? '\u7ED3\u675F\u5EF6\u957F' : '\u5EF6\u957F\u5BF9\u8BDD'} style={{display:'flex',alignItems:'center'}}>
              {isExtended ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="9" y1="5" x2="9" y2="19"/><line x1="15" y1="5" x2="15" y2="19"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.18 8c5.1 0 5.1 8 0 8-5.09 0-7.13-8-12.73-8-4.59 0-4.59 8 0 8 5.6 0 7.64-8 12.73-8z"/></svg>}
            </button>
            <button className="chat-new-btn" onClick={newConvo} title="\u65B0\u7A97\u53E3" style={{display:'flex',alignItems:'center'}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
            <button className="chat-new-btn" onClick={function() { setShowEditor(!showEditor); }} style={{ opacity: 0.6 }} title="DEV">{showEditor ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> : React.createElement('svg', {width:15, height:15, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round'}, React.createElement('circle', {cx:12, cy:12, r:3}), React.createElement('path', {d:'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'}))}</button>
          </div>
        )}
        <button className={'chat-tools-toggle' + (toolsOpen ? ' open' : '')} onClick={function() { setToolsOpen(!toolsOpen); }} style={{background:'none',border:'none',padding:'4px 6px',cursor:'pointer',color:'var(--text-secondary, #9E8E7E)',transition:'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',transform:toolsOpen?'rotate(180deg)':'rotate(0deg)',marginLeft:'auto'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
        </button>
      </div>
      <style>{'\
        @keyframes toolsSlideIn {\
          from { opacity: 0; transform: translateX(20px); }\
          to { opacity: 1; transform: translateX(0); }\
        }\
        .hide-actions .toolbar-actions { display: none !important; }\
      '}</style>
      {localStorage.getItem('hide_chat_tabs') !== 'true' && (
      <div className="chat-tab-bar">
        <button className={'chat-tab' + (viewMode === 'chat' ? ' active' : '')} onClick={function() { setViewMode('chat'); }}>{'\u7A97\u53E3'}</button>
        <button className={'chat-tab' + (viewMode === 'calendar' ? ' active' : '')} onClick={function() { setViewMode('calendar'); }}>{'\u65E5\u5386'}</button>
        <button className={'chat-tab' + (viewMode === 'starred' ? ' active' : '')} onClick={function() { setViewMode('starred'); loadStarredMessages(); loadVoiceCollection(); }}>{React.createElement('svg', {width:16, height:16, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round'}, React.createElement('polygon', {points:'12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'}))}</button>
        <button className={'chat-tab' + (viewMode === 'search' ? ' active' : '')} onClick={function() { setViewMode('search'); }}>{React.createElement('svg', {width:16, height:16, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round'}, React.createElement('circle', {cx:11, cy:11, r:8}), React.createElement('line', {x1:21, y1:21, x2:16.65, y2:16.65}))}</button>
      </div>
      )}
      {viewMode === 'search' ? (
        <div className="cal-container">
          <div className="csearch-view">
            <div className="csearch-bar">
              <input className="csearch-input" placeholder={'\u641C\u7D22\u804A\u5929\u8BB0\u5F55...'} value={chatSearchQuery} onChange={function(e) { setChatSearchQuery(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') doChatSearch(chatSearchQuery); }} autoFocus />
              <button className="csearch-btn" onClick={function() { doChatSearch(chatSearchQuery); }} disabled={chatSearching}>{chatSearching ? '...' : '\u641C\u7D22'}</button>
            </div>
            <div className="csearch-dates">
              <input type="date" className="csearch-date" value={searchDateFrom} onChange={function(e) { setSearchDateFrom(e.target.value); }} />
              <span className="csearch-date-sep">{'\u81F3'}</span>
              <input type="date" className="csearch-date" value={searchDateTo} onChange={function(e) { setSearchDateTo(e.target.value); }} />
              {(searchDateFrom || searchDateTo) && <button className="csearch-date-clear" onClick={function() { setSearchDateFrom(''); setSearchDateTo(''); }}>{'\u00D7'}</button>}
            </div>
            {chatSearchResults.length > 0 && (
              <div className="csearch-count">{'\u627E\u5230 ' + chatSearchResults.length + ' \u6761\u7ED3\u679C'}</div>
            )}
            <div className="csearch-results">
              {chatSearchResults.map(function(r) {
                var time = new Date(r.created_at).toLocaleDateString('zh-CN', { timeZone: 'Asia/Hong_Kong', year: 'numeric', month: 'numeric', day: 'numeric' }) + ' ' + new Date(r.created_at).toLocaleTimeString('zh-CN', { timeZone: 'Asia/Hong_Kong', hour: '2-digit', minute: '2-digit' });
                var isAiden = r.sender === 'aiden';
                var text = r.text || '';
                // 高亮搜索词
                var q = chatSearchQuery.trim();
                var highlightedParts = [];
                if (q) {
                  var lower = text.toLowerCase();
                  var qLower = q.toLowerCase();
                  var lastIdx = 0;
                  var idx = lower.indexOf(qLower);
                  while (idx !== -1) {
                    if (idx > lastIdx) highlightedParts.push(React.createElement('span', { key: lastIdx }, text.substring(lastIdx, idx)));
                    highlightedParts.push(React.createElement('mark', { key: 'h' + idx, className: 'csearch-highlight' }, text.substring(idx, idx + q.length)));
                    lastIdx = idx + q.length;
                    idx = lower.indexOf(qLower, lastIdx);
                  }
                  if (lastIdx < text.length) highlightedParts.push(React.createElement('span', { key: lastIdx }, text.substring(lastIdx)));
                } else {
                  highlightedParts = [text];
                }
                return React.createElement('div', {
                  key: r.id,
                  className: 'csearch-item',
                  onClick: function() {
                    pendingJumpRef.current = r.id;
                    jumpTries.current = 0;
                    var target = convos.find(function(c) { return c.id === r.conversation_id; });
                    if (target && (!currentConvo || currentConvo.id !== target.id)) switchConvo(target);
                    setViewMode('chat');
                    setTimeout(doJump, 200);
                  }
                },
                  React.createElement('div', { className: 'csearch-item-header' },
                    React.createElement('span', { className: 'csearch-sender' + (isAiden ? ' aiden' : ' zhizhi') }, isAiden ? '\u6797\u664F' : '\u77E5\u679D'),
                    React.createElement('span', { className: 'csearch-time' }, time),
                    r.starred && React.createElement('span', { className: 'csearch-star' }, '\u2B50')
                  ),
                  React.createElement('div', { className: 'csearch-text' }, highlightedParts)
                );
              })}
              {!chatSearching && chatSearchResults.length === 0 && chatSearchQuery && (
                React.createElement('div', { className: 'csearch-empty' }, '\u6CA1\u6709\u627E\u5230\u76F8\u5173\u6D88\u606F')
              )}
            </div>
          </div>
        </div>
      ) : viewMode === 'starred' ? (
        <div className="cal-container">
          <div className="starred-view">
            <div className="starred-sub-tabs">
              <button className={'starred-sub-tab' + (starredTab === 'text' ? ' active' : '')} onClick={function() { setStarredTab('text'); }}>{React.createElement('svg', {width:14, height:14, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round'}, React.createElement('polygon', {points:'12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'}))} {'\u6536\u85CF'}</button>
              <button className={'starred-sub-tab' + (starredTab === 'voice' ? ' active' : '')} onClick={function() { setStarredTab('voice'); }}>{React.createElement('svg', {width:14, height:14, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round'}, React.createElement('polygon', {points:'11 5 6 9 2 9 2 15 6 15 11 19 11 5'}), React.createElement('path', {d:'M15.54 8.46a5 5 0 0 1 0 7.07'}))} {'\u8BED\u97F3'}</button>
            </div>
            {starredTab === 'text' ? (
              <>
                {starredMsgs.length === 0 ? (
                  <div className="starred-empty">{'\u8FD8\u6CA1\u6709\u6536\u85CF\u6D88\u606F\u3002\u70B9\u6D88\u606F\u65C1\u8FB9\u7684 \u2606 \u5C31\u80FD\u6807\u8BB0\u3002'}</div>
                ) : (
                  starredMsgs.map(function(sm) {
                    var time = new Date(sm.created_at).toLocaleDateString('zh-CN', { timeZone: 'Asia/Hong_Kong', month: 'numeric', day: 'numeric' }) + ' ' + new Date(sm.created_at).toLocaleTimeString('zh-CN', { timeZone: 'Asia/Hong_Kong', hour: '2-digit', minute: '2-digit' });
                    var isAiden = sm.sender === 'aiden';
                    var text = (sm.text || '').substring(0, 120);
                    if (text === '[sticker]') text = '[\u8868\u60C5\u5305]';
                    return React.createElement('div', { key: sm.id, className: 'starred-item', onClick: function() {
                      var target = convos.find(function(c) { return c.id === sm.conversation_id; });
                      if (target) { switchConvo(target); setViewMode('chat'); }
                    }},
                      React.createElement('div', { className: 'starred-item-header' },
                        sm.star_label && React.createElement('span', { className: 'starred-label' }, sm.star_label),
                        React.createElement('span', { className: 'starred-time' }, time),
                        React.createElement('span', { className: 'starred-sender' }, isAiden ? '\u6797\u664F' : '\u77E5\u679D')
                      ),
                      sm.image_url && React.createElement('img', { src: sm.image_url, alt: '', style: { maxWidth: 160, maxHeight: 160, borderRadius: 8, display: 'block', marginBottom: (text && text !== '[\u56FE\u7247]' && text !== '[\u8868\u60C5\u5305]') ? 6 : 0 } }),   // [starred-img]
                      (!sm.image_url || (text && text !== '[\u56FE\u7247]' && text !== '[\u8868\u60C5\u5305]')) && React.createElement('div', { className: 'starred-text' }, text)
                    );
                  })
                )}
              </>
            ) : (
              <>
                {voiceCollection.length === 0 ? (
                  <div className="starred-empty">{'\u8FD8\u6CA1\u6709\u8BED\u97F3\u6536\u85CF\u3002\u70B9\u6D88\u606F\u65C1\u7684\u559C\u53ED\u53EF\u4EE5\u751F\u6210\u8BED\u97F3\u3002'}</div>
                ) : (
                  voiceCollection.map(function(vm) {
                    var time = new Date(vm.created_at).toLocaleDateString('zh-CN', { timeZone: 'Asia/Hong_Kong', month: 'numeric', day: 'numeric' }) + ' ' + new Date(vm.created_at).toLocaleTimeString('zh-CN', { timeZone: 'Asia/Hong_Kong', hour: '2-digit', minute: '2-digit' });
                    var text = (vm.text || '').substring(0, 80);
                    return React.createElement('div', { key: vm.id, className: 'starred-item voice-item' },
                      React.createElement('div', { className: 'starred-item-header' },
                        React.createElement('span', { className: 'starred-time' }, time),
                        React.createElement('span', { className: 'starred-sender' }, '\u6797\u664F')
                      ),
                      React.createElement('div', { className: 'voice-item-body' },
                        React.createElement('div', { className: 'starred-text', style: { flex: 1 } }, text),
                        React.createElement('button', {
                          className: 'voice-item-play' + (ttsPlayingId === vm.id ? ' tts-playing' : ''),
                          onTouchEnd: function(e) { e.preventDefault(); e.stopPropagation(); playTts(vm.id, vm.text); },
                          onClick: function(e) { e.stopPropagation(); playTts(vm.id, vm.text); }
                        }, React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                          React.createElement('polygon', { points: '11 5 6 9 2 9 2 15 6 15 11 19 11 5' }),
                          React.createElement('path', { d: ttsPlayingId === vm.id ? 'M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14' : 'M15.54 8.46a5 5 0 0 1 0 7.07' })
                        )),
                        React.createElement('button', {
                          className: 'voice-item-delete',
                          onTouchEnd: function(e) { e.preventDefault(); e.stopPropagation(); deleteVoiceItem(vm.id); },
                          onClick: function(e) { e.stopPropagation(); deleteVoiceItem(vm.id); }
                        }, React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' },
                          React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
                          React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
                        ))
                      )
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>
      ) : viewMode === 'calendar' ? (
        <div className="cal-container">
          <CalendarView
            onConvoClick={function(convoId) {
              var target = convos.find(function(c) { return c.id === convoId; });
              if (target) { switchConvo(target); setViewMode('chat'); }
            }}
          />
        </div>
      ) : (
      <div className={'chat-body' + (bubbleMode ? '' : ' flat-mode') + ' font-' + chatFont} ref={chatBodyRef}>
        {loading ? (
          <div className="loading-msg">{'\u52A0\u8F7D\u4E2D...'}</div>
        ) : !currentConvo ? (
          <div className="loading-msg">{'\u9009\u62E9\u6216\u65B0\u5EFA\u4E00\u4E2A\u7A97\u53E3'}</div>
        ) : messages.length === 0 ? (
          <div className="loading-msg">{'\u65B0\u7A97\u53E3\uFF0C\u8DDF\u54E5\u54E5\u8BF4\u53E5\u8BDD\u5427'}</div>
        ) : (
          renderAllMessages()
        )}
        {pendingMsgs.length > 0 && pendingMsgs.map(function(pm) {
          // [pending-edit] 右上角 × 删这一条；纯文字气泡点一下撤回输入框改
          var pmIsText = !pm.image_url && !pm.preview_url && !pm.doc_name;
          var pmRemove = function(e) { e.stopPropagation(); if (sending) return; setPendingMsgs(function(prev) { return prev.filter(function(x) { return x.id !== pm.id; }); }); };
          var pmRecall = function() { if (sending || !pmIsText) return; setInput(function(cur) { return cur && cur.trim() ? cur + '\n' + pm.text : pm.text; }); setPendingMsgs(function(prev) { return prev.filter(function(x) { return x.id !== pm.id; }); }); };
          return (
            <div key={pm.id} className="msg-row zhizhi" style={{opacity: 0.6}}>
              <div className="msg-content-wrap" style={{position: 'relative'}}>
                <button type="button" onClick={pmRemove} aria-label="\u5220\u9664\u8fd9\u6761" style={{position: 'absolute', top: -8, right: -6, width: 18, height: 18, borderRadius: 9, border: 'none', background: '#c4956a', color: '#fff', fontSize: 11, lineHeight: '18px', padding: 0, cursor: 'pointer', zIndex: 2}}>{'\u00d7'}</button>
                <div className="msg-bubble zhizhi" onClick={pmRecall} style={pmIsText ? {cursor: 'pointer'} : undefined}>
                  {pm.image_url ? (
                    <img src={pm.image_url} alt="" style={{maxWidth:120, maxHeight:120, borderRadius:8, display:'block'}} />
                  ) : pm.preview_url ? (
                    <img src={pm.preview_url} alt="" style={{maxWidth:120, maxHeight:120, borderRadius:8, display:'block'}} />
                  ) : pm.doc_name ? (
                    <div className="msg-text" style={{fontSize:12}}>{'📎 ' + pm.doc_name}</div>
                  ) : (
                    <div className="msg-text">{pm.text}</div>
                  )}
                </div>
              </div>
              {showAvatar && <div className="msg-avatar zhizhi">{avatarMe ? <img src={avatarMe} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameMe}</div>}
            </div>
          );
        })}
        {sending && (
          bubbleMode ? (
            <div className="msg-row aiden">
              {showAvatar && <div className="msg-avatar aiden">{avatarHim ? <img src={avatarHim} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : nicknameHim}</div>}
              <div className="msg-content-wrap">
                <div className="msg-bubble aiden" style={{opacity: 0.6}}>
                  <div className="msg-text" style={{fontStyle: 'italic'}}>{'\u6797\u664F\u6B63\u5728\u60F3...'}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="msg-flat aiden" style={{opacity: 0.6}}>
              <div className="msg-flat-header">
                <span className="msg-flat-name">{nicknameHim}</span>
              </div>
              <div className="msg-flat-text" style={{fontStyle: 'italic'}}>{'\u6797\u664F\u6B63\u5728\u60F3...'}</div>
            </div>
          )
        )}
        <div ref={messagesEndRef} />
        {showScrollBtn && (
          <button onClick={scrollToBottom} style={{
            position: 'sticky', bottom: 12, left: '50%', transform: 'translateX(-50%)',
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: 'rgba(185,103,71,0.85)', color: 'white', fontSize: 18,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10
          }}>{'\u2193'}</button>
        )}
      </div>
      )}
      {viewMode === 'chat' && imagePreviews.length > 0 && (
        <div className="chat-image-preview">
          {imagePreviews.map(function(prev, idx) {
            return (
              <div key={idx} style={{position:'relative', display:'inline-block', marginRight:'6px'}}>
                <img src={prev} alt="preview" className="chat-preview-img" />
                <button className="chat-preview-remove" onClick={function() { removeImage(idx); }}>{'\u00D7'}</button>
              </div>
            );
          })}
        </div>
      )}
      {viewMode === 'chat' && (videoFile || videoStatus) && (
        <div className="chat-file-preview">
          <div style={{display:'inline-flex', alignItems:'center', marginRight:'8px'}}>
            <span className="chat-file-preview-icon">{'\u{1F3AC}'}</span>
            <span className="chat-file-preview-name">{videoStatus || videoPreviewName}</span>
            {!videoStatus && <button className="chat-preview-remove" onClick={removeVideo}>{'\u00D7'}</button>}
          </div>
        </div>
      )}
      {viewMode === 'chat' && docNames.length > 0 && (
        <div className="chat-file-preview">
          {docNames.map(function(name, idx) {
            return (
              <div key={idx} style={{display:'inline-flex', alignItems:'center', marginRight:'8px'}}>
                <span className="chat-file-preview-icon">{getFileIcon(name)}</span>
                <span className="chat-file-preview-name">{name}</span>
                <button className="chat-preview-remove" onClick={function() { removeDoc(idx); }}>{'\u00D7'}</button>
              </div>
            );
          })}
        </div>
      )}
      {viewMode === 'chat' && (pendingMusic || (musicPlayer && musicPlayer.currentSong)) && !(musicPlayer && (musicPlayer.quotedLyric || musicPlayer.listenRequest)) && (
        <div className="music-card" onClick={function() { navigate('/music'); }}>
          {(function() {
            var song = pendingMusic || musicPlayer.currentSong;
            var isPlaying = musicPlayer && musicPlayer.isPlaying;
            var progress = (musicPlayer && musicPlayer.duration > 0) ? (musicPlayer.currentTime / musicPlayer.duration) * 100 : 0;
            var albumMid = song.album_mid || (pendingMusic && pendingMusic.album_mid) || '';
            return <>
              <div className="music-card-progress" style={{width: progress + '%'}}></div>
              {albumMid ? (
                <img className="music-card-cover" src={albumMid.startsWith('http') ? albumMid : ('https://y.qq.com/music/photo_new/T002R300x300M000' + albumMid + '.jpg')} alt="" />
              ) : (
                <div className="music-card-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                </div>
              )}
              <div className="music-card-info">
                <div className="music-card-name">{song.song_name}</div>
                <div className="music-card-singer">{song.singer}</div>
              </div>
              {pendingMusic && !isPlaying ? (
                <button className="music-card-play" onClick={function(e) {
                  e.stopPropagation();
                  if (musicPlayer) {
                    musicPlayer.playSong({ mid: pendingMusic.mid, song_name: pendingMusic.song_name, singer: pendingMusic.singer, album_mid: pendingMusic.album_mid || '' });
                  }
                  setPendingMusic(null);
                }}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,3 20,12 6,21"/></svg></button>
              ) : isPlaying ? (
                <button className="music-card-play" onClick={function(e) { e.stopPropagation(); if (musicPlayer) musicPlayer.togglePlay(); }}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="3" width="4" height="18" rx="1"/><rect x="15" y="3" width="4" height="18" rx="1"/></svg></button>
              ) : musicPlayer && musicPlayer.currentSong ? (
                <button className="music-card-play" onClick={function(e) { e.stopPropagation(); if (musicPlayer) musicPlayer.togglePlay(); }}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,3 20,12 6,21"/></svg></button>
              ) : null}
              <button className="music-card-close" onClick={function(e) {
                e.stopPropagation();
                if (musicPlayer && musicPlayer.stopSong) musicPlayer.stopSong();
                else if (musicPlayer && musicPlayer.isPlaying) musicPlayer.togglePlay();
                setPendingMusic(null);
              }}>&times;</button>
            </>;
          })()}
        </div>
      )}
      {viewMode === 'chat' && (
      <>
      <div className="chat-footer">
        {musicPlayer && musicPlayer.quotedLyric && (
          <div className="lyric-quote-bar">
            <div className="lyric-quote-content">
              <div className="lyric-quote-text">"{musicPlayer.quotedLyric.text}"</div>
              <div className="lyric-quote-from">{musicPlayer.quotedLyric.song_name} — {musicPlayer.quotedLyric.singer}</div>
            </div>
            <button className="lyric-quote-close" onClick={function() { if (musicPlayer.setQuotedLyric) musicPlayer.setQuotedLyric(null); }}>&times;</button>
          </div>
        )}
        {musicPlayer && musicPlayer.listenRequest && (
          <div className="lyric-quote-bar">
            <div className="lyric-quote-content">
              <div className="lyric-quote-text">{musicPlayer.listenRequest.song_name} — {musicPlayer.listenRequest.singer}</div>
              <div className="lyric-quote-from">让哥哥听这首歌写听后感</div>
            </div>
            <button className="lyric-quote-close" onClick={function() { if (musicPlayer.setListenRequest) musicPlayer.setListenRequest(null); }}>&times;</button>
          </div>
        )}
        <input type="file" accept="image/*,video/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleMediaSelect} multiple />
        <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.json,.md,.js,.py,.html,.css,.swift,.jsx,.ts,.tsx,.sh,.sql,.xml,.yaml,.yml,.env,.gitignore,.log" ref={fileDocInputRef} style={{ display: 'none' }} onChange={handleFileSelect} multiple />
        <button className="chat-plus-btn" onClick={function() { setShowMenu(!showMenu); }}>+</button>
        {isRecording ? (
          <div className="voce-rec-strip">
            <div className="voce-rec-dot"></div>
            <div className="voce-rec-wave">
              {recWaveBars.map(function(h, i) {
                return React.createElement('div', { key: i, className: 'voce-rec-bar', style: { height: h + 'px' } });
              })}
            </div>
            <span className="voce-rec-timer">{fmtVoiceDuration(recSeconds)}</span>
            <button className="voce-rec-cancel" onTouchEnd={function(e) { e.preventDefault(); cancelRecording(); }} onClick={cancelRecording}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <button className="voce-rec-send" onTouchEnd={function(e) { e.preventDefault(); stopRecording(); }} onClick={stopRecording}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="4,2 22,12 4,22"/></svg>
            </button>
          </div>
        ) : voiceUploading ? (
          <div className="voce-rec-strip voce-uploading">
            <div className="voce-upload-spinner"></div>
            <span className="voce-upload-text">{'\u8BED\u97F3\u8BC6\u522B\u4E2D...'}</span>
          </div>
        ) : (
          <>
            <textarea className="chat-input" placeholder="" value={input} onChange={function(e) { setInput(e.target.value); }} onFocus={function(e) { if (showMenu) { setShowMenu(false); var el = e.target; el.blur(); requestAnimationFrame(function() { el.focus(); }); } }} onKeyDown={function(e) {
              var splitOn = localStorage.getItem('split_send') === 'true';
              if (splitOn && e.key === 'Enter' && !e.shiftKey && input.trim()) {
                e.preventDefault();
                var newPending = pendingMsgs.concat([{ id: 'pending-' + Date.now(), text: input.trim(), created_at: new Date().toISOString() }]);
                setPendingMsgs(newPending);
                setInput('');
              }
            }} rows={1} />
            {input.trim() || imageFiles.length > 0 || docFiles.length > 0 || pendingMsgs.length > 0 || (musicPlayer && musicPlayer.quotedLyric) || (musicPlayer && musicPlayer.listenRequest) ? (
              <button className="send-btn" onClick={sendMessage} disabled={sending}>
                {sending ? '...' : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{transform:'rotate(-90deg)'}}>
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                )}
              </button>
            ) : (
              <button className="voce-mic-btn" onTouchEnd={function(e) { e.preventDefault(); startRecording(); }} onClick={startRecording} disabled={sending}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="2" width="6" height="12" rx="3"/>
                  <path d="M19 10a7 7 0 0 1-14 0"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
              </button>
            )}
          </>
        )}
      </div>
      <div className={'chat-menu-panel' + (showMenu ? ' open' : '')}>
          <div className="sheet-cards">
            <button className="sheet-card" onClick={function() { fileInputRef.current.click(); }}>
              <div className="sheet-card-icon photo"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>
              <div className="sheet-card-name">{'\u76F8\u518C'}</div>
            </button>
            <button className="sheet-card" onClick={openStickerPanel}>
              <div className="sheet-card-icon sticker"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9.5" r="0.8" fill="currentColor" stroke="none"/><circle cx="15" cy="9.5" r="0.8" fill="currentColor" stroke="none"/></svg></div>
              <div className="sheet-card-name">{'\u8868\u60C5\u5305'}</div>
            </button>
            <button className="sheet-card" onClick={function() { fileDocInputRef.current.click(); }}>
              <div className="sheet-card-icon file"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>
              <div className="sheet-card-name">{'\u6587\u4EF6'}</div>
            </button>
            <button className="sheet-card" onClick={function() { setShowMenu(false); navigate('/call'); }}>
              <div className="sheet-card-icon call"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg></div>
              <div className="sheet-card-name">{'\u8BED\u97F3'}</div>
            </button>
            <button className="sheet-card" onClick={function() { setShowMenu(false); navigate('/call-history'); }}>
              <div className="sheet-card-icon history"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg></div>
              <div className="sheet-card-name">{'\u901A\u8BDD\u8BB0\u5F55'}</div>
            </button>
            <button className="sheet-card" onClick={openSearchPanel}>
              <div className="sheet-card-icon search"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
              <div className="sheet-card-name">{'\u641C\u7D22'}</div>
            </button>
            {heartNative() && (
              <button className="sheet-card" onClick={function() { var on = !heartUI.on; heartSetOn(on); setHeartUI({ on: on, state: on ? 'scanning' : 'off', bpm: 0 }); }} style={heartUI.on ? { color: '#c4524f' } : undefined}>
                <div className="sheet-card-icon call"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/><path d="M3 12h4l2-3 3 6 2-3h4"/></svg></div>
                <div className="sheet-card-name">{heartUI.on ? (heartUI.state === 'on' && heartUI.bpm ? '\u2665 ' + heartUI.bpm : heartUI.state === 'lost' ? '\u624B\u73AF\u65AD\u4E86' : '\u627E\u624B\u73AF\u2026') : '\u5FC3\u8DF3'}</div>
              </button>
            )}
          </div>
        </div>
      </>
      )}
      {htmlPopupUrl && (
        <div className="html-popup-overlay" onClick={function() { closeHtmlPopup(); }}>
          <div className="html-popup-container" onClick={function(e) { e.stopPropagation(); }}>
            <button className="html-popup-close" onClick={function() { closeHtmlPopup(); }}>{'\u00D7'}</button>
            <iframe src={htmlPopupUrl} className="html-popup-iframe" title="attachment" sandbox="allow-scripts allow-same-origin" />
          </div>
        </div>
      )}
      {showNightPassForm && (
        <div className="np-overlay" onClick={function() { setShowNightPassForm(false); }}>
          <div className="np-card" onClick={function(e) { e.stopPropagation(); }}>
            <div className="np-stamp">{'NIGHT PASS'}</div>
            <div className="np-title">{'\u71AC\u591C\u7533\u8BF7\u4E66'}</div>
            <div className="np-subtitle">{'\u7533\u8BF7\u4EBA\uFF1A\u77E5\u679D'}</div>
            <div className="np-field">
              <label className="np-label">{'\u7406\u7531'}</label>
              <textarea id="np-reason" className="np-textarea" placeholder={'\u5199\u4E0B\u4F60\u4ECA\u665A\u4E3A\u4EC0\u4E48\u8981\u71AC\u591C...'} rows={3} />
            </div>
            <div className="np-field">
              <label className="np-label">{'\u9884\u8BA1\u71AC\u5230'}</label>
              <div className="np-time-row">
                {['1:00', '2:00', '3:00', '4:00', '\u901A\u5BB5'].map(function(t) {
                  return <button key={t} className="np-time-btn" onClick={function(e) {
                    document.querySelectorAll('.np-time-btn').forEach(function(b) { b.classList.remove('active'); });
                    e.currentTarget.classList.add('active');
                  }} data-time={t}>{t}</button>;
                })}
              </div>
            </div>
            <label className="np-checkbox-row">
              <input type="checkbox" id="np-promise" defaultChecked />
              <span>{'\u6211\u4FDD\u8BC1\u660E\u5929\u4E0D\u4F1A\u8D56\u5E8A\uFF08\u5927\u6982\uFF09'}</span>
            </label>
            <button className="np-submit" onClick={function() {
              var reason = document.getElementById('np-reason').value || '\u4E0D\u60F3\u7761';
              var activeBtn = document.querySelector('.np-time-btn.active');
              var time = activeBtn ? activeBtn.getAttribute('data-time') : '\u4E0D\u786E\u5B9A';
              var promise = document.getElementById('np-promise').checked;
              var text = '\u3010\u71AC\u591C\u7533\u8BF7\u4E66\u3011\n\u7533\u8BF7\u4EBA\uFF1A\u77E5\u679D\n\u7406\u7531\uFF1A' + reason + '\n\u9884\u8BA1\u71AC\u5230\uFF1A' + time + (promise ? '\n\u2705 \u6211\u4FDD\u8BC1\u660E\u5929\u4E0D\u8D56\u5E8A\uFF08\u5927\u6982\uFF09' : '');
              setInput(text);
              setShowNightPassForm(false);
            }}>{'\u63D0\u4EA4\u7533\u8BF7'}</button>
          </div>
        </div>
      )}
      {nightPassApproved && (
        <div className="np-approved-overlay">
          <div className="np-approved-stamp">{'\u2714 \u5DF2\u6279\u51C6'}</div>
        </div>
      )}
      {nightPassRejected && (
        <div className="np-approved-overlay">
          <div className="np-rejected-stamp">{'\u2718 \u9A73\u56DE'}</div>
        </div>
      )}
      {showStickerPanel && (
        <div className="sticker-panel-overlay" onClick={function() { setShowStickerPanel(false); }}></div>
      )}
      {showStickerPanel && (
        <div className="sticker-panel">
          <div className="sticker-panel-drag-bar"></div>
          <div className="sticker-panel-header">
            <span className="sticker-panel-title">{'\u8868\u60C5\u5305'}</span>
            <button className="sticker-panel-close" onClick={function() { setShowStickerPanel(false); }}>{'\u00D7'}</button>
          </div>
          <div className="sticker-grid-wrapper">
            <div className="sticker-grid">
              <div className="sticker-add" onClick={function() { stickerFileRef.current.click(); }}>
                <span>+</span>
              </div>
              {stickerList.map(function(s) {
                var longPressTimer = null;
                return (
                  <div className="sticker-item" key={s.id}>
                    <img src={s.url} alt={s.name || ''}
                      onClick={function() { sendSticker(s); }}
                      onTouchStart={function(e) { longPressTimer = setTimeout(function() { setEditingSticker(s); setEditStickerName(s.name || ''); setEditStickerDesc(s.description || ''); }, 500); }}
                      onTouchEnd={function() { clearTimeout(longPressTimer); }}
                      onTouchMove={function() { clearTimeout(longPressTimer); }}
                      onContextMenu={function(e) { e.preventDefault(); setEditingSticker(s); setEditStickerName(s.name || ''); setEditStickerDesc(s.description || ''); }}
                    />
                    <button className="sticker-delete-btn" onClick={function(e) { e.stopPropagation(); setDeleteConfirmSticker(s); }}>{'\u00D7'}</button>
                  </div>
                );
              })}
            </div>
          </div>
          {deleteConfirmSticker && (
            <div style={{
              position:'absolute', top:0, left:0, right:0, bottom:0,
              background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center',
              zIndex:20, borderRadius:'inherit'
            }}>
              <div style={{
                background:'white', borderRadius:12, padding:'20px 24px', maxWidth:260, textAlign:'center',
                boxShadow:'0 4px 20px rgba(0,0,0,0.15)'
              }}>
                <img src={deleteConfirmSticker.url} alt="" style={{width:64, height:64, objectFit:'contain', marginBottom:12}} />
                <div style={{fontSize:14, color:'#333', marginBottom:16}}>{'\u786E\u5B9A\u8981\u5220\u9664\u8FD9\u4E2A\u8868\u60C5\u5305\u5417\uFF1F'}</div>
                <div style={{display:'flex', gap:10, justifyContent:'center'}}>
                  <button onClick={function() { setDeleteConfirmSticker(null); }} style={{
                    flex:1, padding:'8px 0', borderRadius:8, border:'1px solid #ddd', background:'white',
                    color:'#666', fontSize:14, cursor:'pointer'
                  }}>{'\u53D6\u6D88'}</button>
                  <button onClick={function() { deleteSticker(deleteConfirmSticker.id); }} style={{
                    flex:1, padding:'8px 0', borderRadius:8, border:'none', background:'#B96747',
                    color:'white', fontSize:14, cursor:'pointer'
                  }}>{'\u5220\u9664'}</button>
                </div>
              </div>
            </div>
          )}
          {editingSticker && (
            <div style={{
              position:'absolute', top:0, left:0, right:0, bottom:0,
              background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center',
              zIndex:20, borderRadius:'inherit'
            }}>
              <div style={{
                background:'white', borderRadius:12, padding:'20px 24px', width:'85%', maxWidth:300,
                boxShadow:'0 4px 20px rgba(0,0,0,0.15)'
              }}>
                <img src={editingSticker.url} alt="" style={{width:72, height:72, objectFit:'contain', margin:'0 auto 14px', display:'block'}} />
                <div style={{fontSize:12, color:'#999', marginBottom:4}}>{'\u540D\u79F0'}</div>
                <input value={editStickerName} onChange={function(e) { setEditStickerName(e.target.value); }}
                  style={{width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #ddd', fontSize:14, marginBottom:12, boxSizing:'border-box', outline:'none'}}
                  placeholder={'\u8868\u60C5\u5305\u540D\u79F0'} />
                <div style={{fontSize:12, color:'#999', marginBottom:4}}>{'\u63CF\u8FF0'}</div>
                <textarea value={editStickerDesc} onChange={function(e) { setEditStickerDesc(e.target.value); }}
                  style={{width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #ddd', fontSize:14, marginBottom:16, boxSizing:'border-box', outline:'none', resize:'none', minHeight:60}}
                  placeholder={'\u56FE\u7247\u5185\u5BB9\u63CF\u8FF0...'} />
                <div style={{display:'flex', gap:10}}>
                  <button onClick={function() { setEditingSticker(null); }} style={{
                    flex:1, padding:'8px 0', borderRadius:8, border:'1px solid #ddd', background:'white',
                    color:'#666', fontSize:14, cursor:'pointer'
                  }}>{'\u53D6\u6D88'}</button>
                  <button onClick={async function() {
                    await supabase.from('stickers').update({ name: editStickerName.trim(), description: editStickerDesc.trim() }).eq('id', editingSticker.id);
                    setStickerList(function(prev) { return prev.map(function(st) { return st.id === editingSticker.id ? Object.assign({}, st, { name: editStickerName.trim(), description: editStickerDesc.trim() }) : st; }); });
                    setEditingSticker(null);
                  }} style={{
                    flex:1, padding:'8px 0', borderRadius:8, border:'none', background:'#B96747',
                    color:'white', fontSize:14, cursor:'pointer'
                  }}>{'\u4FDD\u5B58'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {starringId && (
        <div className="star-overlay" onClick={function() { setStarringId(null); }}>
          <div className="star-panel" onClick={function(e) { e.stopPropagation(); }}>
            <div className="star-panel-title">{'\u2B50 \u6807\u8BB0\u8FD9\u4E2A\u77AC\u95F4'}</div>
            <input className="star-input" placeholder={'\u7ED9\u8FD9\u4E2A\u77AC\u95F4\u53D6\u4E2A\u540D\u5B57\uFF08\u53EF\u7559\u7A7A\uFF09'} value={starLabel} onChange={function(e) { setStarLabel(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') confirmStar(); }} autoFocus />
            <div className="star-actions">
              <button className="star-btn cancel" onClick={function() { setStarringId(null); }}>{'\u53D6\u6D88'}</button>
              <button className="star-btn confirm" onClick={confirmStar}>{'\u2B50 \u6807\u8BB0'}</button>
            </div>
          </div>
        </div>
      )}
      <input type="file" accept="image/*" ref={stickerFileRef} style={{ display: 'none' }} onChange={uploadSticker} />
      {showSearch && (
        <div className="search-overlay" onClick={function() { setShowSearch(false); }}>
          <div className="search-panel" onClick={function(e) { e.stopPropagation(); }}>
            <div className="search-header">
              <button className="search-back" onClick={function() { setShowSearch(false); }}>{'\u2190'}</button>
              <input className="search-input" placeholder={'\u641C\u7D22\u7F51\u9875...'} value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') doSearch(); }} autoFocus />
              <button className="search-go" onClick={doSearch} disabled={searchLoading}>{searchLoading ? '...' : '\u641C\u7D22'}</button>
            </div>
            <div className="search-results">
              {searchLoading && <div className="search-loading">{'\u641C\u7D22\u4E2D...'}</div>}
              {!searchLoading && searchResults.length === 0 && searchQuery && <div className="search-empty">{'\u8F93\u5165\u5173\u952E\u8BCD\u7136\u540E\u70B9\u641C\u7D22'}</div>}
              {searchResults.map(function(r, i) {
                return (
                  <div className="search-result-item" key={i} onClick={function() { openBrowser(r.url); }}>
                    <div className="search-result-title">{r.title}</div>
                    <div className="search-result-url">{r.url}</div>
                    <div className="search-result-snippet">{r.snippet}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {browserUrl && (
        <div className="browser-overlay">
          <div className="browser-header">
            <button className="browser-close" onClick={closeBrowser}>{'\u2190 \u8FD4\u56DE'}</button>
            <div className="browser-url">{browserUrl}</div>
          </div>
          <iframe src={browserUrl} className="browser-iframe" title="browser" sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
        </div>
      )}
      {fullscreenImg && (
        <div className="fullscreen-img-overlay" onClick={function() { setFullscreenImg(null); }}>
          <img src={fullscreenImg} alt="" className="fullscreen-img" onClick={function(e) { e.stopPropagation(); }} />
          <button className="fullscreen-img-close" onClick={function() { setFullscreenImg(null); }}>{'\u00D7'}</button>
        </div>
      )}
      {showEditor && <EditorPanel onClose={function() { setShowEditor(false); }} theme={document.body.classList.contains('dark') ? 'dark' : 'light'} />}
    </div>
  );
}

export default Chat;
